export const personas = {
  common: {
    waitlistButton: "Start now – free",
    bookingNote: "* Clients can still book appointments by phone or through your website in the traditional way if they prefer.",
    scaleNote: "Bespire is perfect for small and large teams - scalable from 1 to 20+ professionals",
  },
  sections: {
    solo: {
      badge: "For Solo Professionals",
      title: "Perfect for solo professionals",
      description: "If you work alone, Bespire AI is your perfect assistant. It handles appointments, clients, and marketing - so you can focus on what you love.",
    },
    team: {
      badge: "For Multi-Professional Teams",
      title: "Complex team management made simple",
      description: "If multiple professionals work together, Bespire helps coordinate the team.",
    },
    bookingSystem: {
      badge: "All features in one place",
      title: "Professional booking system for every need",
      description: "Whether for individual sessions, couples services, or complex teams - the system adapts flexibly to you.",
    },
    noDownload: {
      badge: "Easy for clients too",
      title: "Your clients don't need to download anything",
      description: "No more widget embedding, no redirecting to external systems, no separate app installation. Everything happens on a single, perfectly mobile-optimized interface - under your branding.",
      features: {
        noApp: {
          title: "No app installation",
          description: "Clients book, pay, and communicate from the browser - with a single click",
        },
        noWidget: {
          title: "No external widget",
          description: "The booking process is fully integrated, no redirection to another page",
        },
        unified: {
          title: "Unified experience",
          description: "Same design, same interface - from booking to payment, everything is your brand",
        },
      },
      phoneMockup: {
        url: "fitpro.com/booking",
        businessName: "FitPro Studio",
        tagline: "Personal training & group classes",
        selectService: "Select a service",
        services: [
          { name: "Personal Training", duration: "60 min", price: "$80" },
          { name: "Group Class", duration: "45 min", price: "$25" },
          { name: "Online Coaching", duration: "30 min", price: "$50" },
        ],
        selectTime: "Select a time",
        timeSlots: ["08:00", "09:30", "10:00", "11:30", "14:00", "16:00"],
        confirmed: "Booking confirmed!",
        confirmedSubtext: "We'll send you an SMS reminder",
        browserLabel: "From browser, no app needed",
        noAppNeeded: "No app download needed!",
      },
    },
    aiChat: {
      sectionTitle: "AI Chat in Action",
      sectionSubtitle: "Here are some examples of how the AI chat works in everyday tasks",
      websiteBuilding: {
        badge: "Website building",
        title: "Chat-based website creation",
        description: "You talk to the AI and it builds your website. No coding, no complicated editor - just natural conversation.",
        features: [
          "Add/modify sections through conversation",
          "Easy customization of colors and fonts",
          "Gallery, pricing, services automatically",
        ],
        aiAction: "AI creates the services section",
      },
      calendar: {
        badge: "Appointment management",
        title: "AI manages your calendar",
        description: "The AI assistant completely takes over booking, rescheduling, and reminder management. You just tell it what you want.",
        features: [
          "Add and modify appointments via chat",
          "Automatic reminder sending",
          "Instant client history retrieval",
        ],
        aiAction: "AI instantly shows available slots",
      },
      marketing: {
        badge: "Marketing AI",
        title: "Automatic marketing campaigns",
        description: "AI creates social media posts, email campaigns, and WhatsApp messages for you. You just approve them.",
        features: [
          "Weekly Instagram and Facebook posts",
          "Email campaigns for inactive clients",
          "Seasonal offer advertising",
        ],
        aiAction: "AI creates and targets the campaign",
      },
      bottomHighlight: {
        title: "Everything chat-based, in one place",
        description: "You don't need 10 different tools for website building, calendar management, marketing. The AI chat handles everything - you just talk to it naturally, like an assistant, and it solves everything for you in the background.",
      },
    },
    bookingFlow: {
      badge: "Booking process",
      title: "Appointment booking with AI chat step by step",
      subtitle: "See how easy it is for your clients to book an appointment",
      stepLabel: "Step",
      steps: [
        {
          step: "1",
          title: "Client writes to AI",
          description: "The client writes to the AI assistant on your website or WhatsApp in natural language",
        },
        {
          step: "2",
          title: "AI understands and suggests",
          description: "The AI checks your calendar, sees available slots, and offers them to the client",
        },
        {
          step: "3",
          title: "Automatic confirmation",
          description: "Client chooses, AI adds to calendar and sends confirmation email and SMS",
        },
        {
          step: "4",
          title: "Reminder 24h before",
          description: "24 hours before, automatic reminder, client can confirm or modify",
        },
      ],
    },
    marketing: {
      title: "Marketing that runs itself",
      description: "Automated promotions, seasonal offers, and feedback requests - AI times and personalizes everything for you.",
      features: [
        {
          title: "Social media automation",
          description: "AI creates 3 Instagram and Facebook posts per week from your gallery. Hashtags, descriptions, scheduled publishing - all automatic.",
        },
        {
          title: "Email marketing",
          description: "Automatic emails for new clients, callback messages for inactive clients, birthday greetings with gift coupons.",
        },
        {
          title: "WhatsApp messages",
          description: "Last minute offers, seasonal deals, callback messages - short, effective communication on WhatsApp.",
        },
        {
          title: "TikTok campaign suggestions",
          description: "AI analyzes your trends and makes specific suggestions for TikTok content, video ideas, hashtag strategies - you decide what to use.",
        },
      ],
      mockup: {
        header: "Marketing Automation",
        subheader: "AI-powered campaigns",
        aiActive: "AI active",
        aiDetected: "AI detection",
        opportunity: "Found 28 inactive clients who haven't booked in 2+ months. Suggesting campaigns across 3 channels.",
        campaignsReady: "Campaigns ready",
        campaigns: [
          { channel: "Email", message: "\"We miss you! 🎁 Here's a free session coupon...\"", audience: "28 people" },
          { channel: "WhatsApp", message: "\"Hey! We have a gift for you 💪\"", audience: "22 people" },
          { channel: "Instagram", message: "\"Welcome back! Limited offer...\"", audience: "1.2K" },
        ],
        schedule: "Schedule",
        scheduleDays: ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"],
        sent: "Campaigns launched!",
        sentDetail: "3 channels · 28 clients targeted",
        results: "Results",
        live: "● Live",
        stats: [
          { value: "82%", label: "Open rate" },
          { value: "12", label: "Re-booked" },
          { value: "$1.2K", label: "Revenue" },
        ],
        roiTitle: "12x ROI automatically",
        roiSubtitle: "Fully AI-managed, no intervention needed",
      },
    },
    finances: {
      badge: "Finances",
      title: "Transparent finances",
      description: "Online payment, automatic invoicing, detailed reports - all in one place",
      features: [
        {
          title: "Revenue management",
          description: "Daily, weekly, monthly revenue overview. Breakdown by professional in teams.",
        },
        {
          title: "Online payment",
          description: "Advance payment by card, on-site payment, automatic invoicing.",
        },
        {
          title: "Packages and passes",
          description: "Passes, seasonal packages, gift voucher sales.",
        },
      ],
      stats: {
        daily: "Daily",
        weekly: "Weekly",
        monthly: "Monthly",
        revenue: "revenue",
        statistics: "statistics",
        report: "report",
      },
    },
    sellProducts: {
      badge: "Sales opportunities",
      title: "What can you sell?",
      description: "Multiple revenue streams, one platform - with Bespire you can sell not just services, but digital products, courses, and consulting too",
    },
    cta: {
      title: "Ready to make the switch?",
      description: "Ready for the switch? Launch your site now by chatting with the AI, or request a callback and we'll build it for you in 48 hours.",
      button: "Start now – free",
    },
  },
  bookingSystem: {
    tickets: {
      title: "Passes and payment",
      flexibleTickets: {
        title: "Flexible pass system",
        description: "Session-based, time-limited, unlimited passes - automatic deduction when booking.",
      },
      onsitePayment: {
        title: "On-site payment",
        description: "Cash or card on location - tracking of pending payments.",
      },
      invoicing: {
        title: "Invoicing setup",
        description: "Linked to billing center or professionals - perfect for subcontractors too.",
      },
    },
    staff: {
      title: "Staff and permissions",
      accounts: {
        title: "Staff accounts",
        description: "Own calendar, bookings, clients - everyone only sees what they have access to.",
      },
      transfer: {
        title: "Easy session transfer",
        description: "Staff member sick? A few clicks and clients are automatically notified.",
      },
      unlimited: {
        title: "Unlimited staff",
        description: "Add as many as you want, you don't pay more for it.",
      },
    },
    clients: {
      title: "Client management and tagging",
      tags: {
        title: "Free tagging",
        description: "VIP, athlete, chronic... The AI will also tag automatically over time.",
      },
      newClient: {
        title: "New client registration",
        description: "Not registered? Record them simply right away.",
      },
      bulkImport: {
        title: "Bulk import",
        description: "Up to 50 clients at once with import.",
      },
    },
    services: {
      title: "Services and room management",
      visibility: {
        title: "Targeted visibility",
        description: "Control who sees what based on tags.",
      },
      rooms: {
        title: "Room management",
        description: "Multiple rooms, conflict detection, optimal utilization.",
      },
    },
    coupons: {
      title: "Coupons and partnerships",
      codes: {
        title: "Coupon codes",
        description: "Percentage, fixed amount discounts - with expiration and limits.",
      },
      corporate: {
        title: "Corporate partnerships",
        description: "Email domain-based coupons with automatic redemption.",
      },
    },
    automation: {
      title: "Automations and rules",
      cancellation: {
        title: "Automatic cancellations",
        description: "Rules: how long to cancel, warnings, deletion of unpaid bookings.",
      },
      waitlist: {
        title: "Waitlist",
        description: "Day fully booked? Automatic notification to the first person waiting when a slot opens.",
      },
      logic: {
        title: "Booking logic",
        description: "Advance booking, minimum time, free cancellation - all customizable.",
      },
    },
    communication: {
      title: "Communication",
      templates: {
        title: "Email templates",
        description: "Confirmation, reminder, cancellation - in your style, with your logo.",
      },
      whatsapp: {
        title: "WhatsApp notifications",
        description: "Automatic messages to clients, where they actually read them.",
      },
      targeted: {
        title: "Targeted sending",
        description: "To VIPs, expiring pass holders, inactive clients - based on tags.",
      },
    },
  },
  soloFeatures: {
    websiteBuilder: {
      title: "Build your website with AI",
      description: "You chat with the AI and it builds your website. No coding, no complicated editor - just tell it what you want and the AI does it. If you already have a website and system, no problem: just enter the URL and Bespire imports images, descriptions, everything - and builds you a beautiful new website in minutes.",
      details: [
        "Chat-based website building, zero coding",
        "Import existing website by URL",
        "Easy customization of colors and fonts",
        "Gallery, pricing, services automatically",
      ],
    },
    calendar: {
      title: "Chat-based calendar management",
      description: "You simply write to the AI assistant via chat when you want to add, modify, or delete appointments. No need to search through complicated admin panels - just describe what you want and the AI does it.",
      details: [
        "Add appointments with simple messages",
        "Manage services and durations",
        "Calendar overview in natural language",
        "Automatic conflict handling and warnings",
      ],
    },
    clientManagement: {
      title: "Shared client and professional interface",
      description: "All client history in one place: booking history, treatments, personal notes. Clients also get their own interface where they can see their passes, bookings, and history. Both sides are up to date, the system always knows so no one has to remember.",
      details: [
        "Complete treatment history per client",
        "Client's own interface with passes and history",
        "Personal notes and information",
        "Contact history from both sides",
      ],
    },
    marketing: {
      title: "Marketing automation",
      description: "AI creates social media posts, email campaigns. For example: Bring back those who haven't visited you in 2 months. You just approve, the rest is automatic.",
      details: [
        "Weekly Instagram and Facebook posts",
        "Email campaigns for inactive clients",
        "Automatic birthday greetings",
        "Seasonal promotion advertising",
      ],
    },
  },
  teamFeatures: {
    multiStaff: {
      title: "Easy management of multiple staff",
      description: "Every staff member with their own calendar, client list, statistics. You see everything from the central admin panel, but everyone only sees what they should.",
      benefits: [
        "Separate calendar and client base per staff member",
        "Central dashboard for the owner",
        "Permission management: owner, manager, employee",
        "Real-time synchronization",
      ],
    },
    aiCoordination: {
      title: "AI coordinates the team",
      description: "The AI automatically distributes bookings among staff members. It considers who does which service, who is available when.",
      benefits: [
        "One booking - automatic staff assignment",
        "Automatic booking distribution",
        "Service-based assignment",
        "Redirection if someone is sick or absent",
      ],
    },
    statistics: {
      title: "Transparent statistics",
      description: "Which staff member worked how much, who brought how much revenue, which service is most popular. All data in one place, in exportable reports.",
      benefits: [
        "Performance statistics per staff member",
        "Revenue reports daily/weekly/monthly",
        "Service popularity top list",
        "Export to Excel and PDF format",
      ],
    },
    branding: {
      title: "Fully custom branding, throughout",
      description: "No embedded external booking widget that sticks out from the design. Everything with unified appearance, your brand, the way you want it.",
      benefits: [
        "Own logo, colors, fonts everywhere",
        "No foreign widget or popup",
        "Unified experience from booking to payment",
        "Professional appearance at every touchpoint",
      ],
    },
  },
  // Persona-specific content
  massageTherapists: {
    slug: "massage-therapist",
    heroTitle: "AI assistant that helps your daily work",
    heroAnimation: {
      chat: {
        header: "Bespire AI Assistant",
        subheader: "Online · Always available",
        inputPlaceholder: "Type a message...",
        user1: "Build me a website with massage treatments and pricing!",
        ai1: "Done! I've built your site with 3 sections: hero, massages and pricing. Here's your URL: zenwellness.bespire.com",
        user2: "Book Kovács Éva for Swedish massage tomorrow at 2 PM!",
        ai2: "Booked! Kovács Éva - Swedish massage, tomorrow 2:00 PM. I've sent her a notification too.",
        user3: "Send an email to clients who haven't visited in 2 months!",
        ai3: "Campaign created and sent to 31 inactive clients. Already at 85% open rate!",
        user4: "Thanks, this is fantastic! 😍",
        ai4: "You're welcome! If you need anything, just ask. Website, calendar, clients, marketing - you just massage! 💆",
      },
    },
    websiteAnimation: {
      chatHeader: "Website Builder",
      chatSubheader: "AI assistant",
      domain: "zenwellness.bespire.com",
      chatPrompt: "Build my page with massages and pricing!",
      aiMsg1: "Sure! I've started building your site. Just a few seconds and it'll be ready...",
      aiMsg2: "Sections are done:",
      doneLabel: "Done! Your site is live and available:",
      userFollowUp: "Can you change the colors to gold and cream tones?",
      aiFollowUpReply: "Done! I've switched the colors to gold and cream tones. Looks beautiful! 🌿",
      blocks: {
        hero: "Hero section",
        services: "Massage treatments",
        pricing: "Pricing",
      },
    },
    calendarAnimation: {
      chatHeader: "Calendar Management",
      month: "March 2026",
      subtitle: "AI assistant",
      synced: "Synced",
      freeLabel: "Free",
      userAsk: "What are my free slots tomorrow?",
      aiResponse: "Your tomorrow's schedule:",
      userBook: "Book the 10:30 slot for Kovács Éva for Swedish massage!",
      aiConfirm: "Done! 10:30 booked for Kovács Éva - Swedish massage",
      notifSent: "Notification sent to client",
      userThanks: "Thanks, great! 👍",
      days: ["M", "T", "W", "Th", "F", "Sa", "Su"],
      slots: {
        slot1: "Nagy A. - Swedish massage",
        slot2: "Tóth P. - Sports massage",
        slot3: "",
        slot4: "Kiss M. - Thai massage",
        slot5: "",
      },
      newBookingNotif: "New booking: Szabó L. - Relaxation 3:30 PM",
    },
    clientsAnimation: {
      chatHeader: "Client Management",
      chatSubheader: "AI assistant",
      userAsk: "Show me Veres Emese's profile!",
      userAction: "Send her a reminder about her expiring pass!",
      aiConfirm: "Done! Reminder sent to Veres Emese",
      visitsLabel: "visits",
      clients: {
        client1: { name: "Veres Emese", tag: "VIP", status: "Active pass" },
        client2: { name: "Kovács Éva", tag: "New", status: "First massage" },
        client3: { name: "Nagy Anna", tag: "VIP", status: "10-session" },
        client4: { name: "Tóth Péter", tag: "Returning", status: "Monthly pass" },
      },
      tabs: {
        profile: "Profile",
        history: "History",
        notes: "Notes",
      },
      timeline: {
        firstVisit: "First visit",
        passBought: "10-session pass",
        session: "Swedish massage #7",
        renewal: "Pass renewal",
      },
      stats: {
        retention: "Retention",
        vipClients: "VIP clients",
        growth: "Growth",
      },
    },
    marketingAnimation: {
      chatHeader: "Marketing",
      title: "Marketing automation",
      subtitle: "AI assistant",
      userAsk: "Bring back clients inactive for 2 months!",
      aiCampaignIntro: "I've created the campaign! Here's the email preview:",
      emailSubject: "We miss you!",
      emailPreview: "20% discount for returning clients",
      emailCta: "Book now!",
      userSend: "Send it now! 🚀",
      aiSentConfirm: "Sent to all 31 contacts!",
      userReply: "This is fantastic! 😍",
      sentLabel: "Sent",
      opensLabel: "Opens",
      funnel: {
        audience: "Audience",
        audienceDetail: "31 inactive clients",
        content: "Content",
        contentDetail: "Comeback offer email",
        sending: "Sending",
        sendingDetail: "Sent to 31 contacts",
        results: "Results",
        resultsDetail: "85% open rate",
        resultTitle: "Campaign results",
        live: "● Live",
        conversions: "Returning",
      },
    },
    teamAnimation: {
      chatHeader: "Team Management",
      chatSubheader: "AI assistant · Online",
      inputPlaceholder: "Type a message...",
      user1: "Show me today's team! Who's working?",
      ai1: "Here's today's team status:",
      user2: "How are today's bookings looking?",
      ai2: "Here are today's stats:",
      user3: "Varga Lili is sick, redistribute her treatments to the others!",
      ai3: "I've reassigned Varga Lili's 4 treatments to available therapists.",
      user4: "Great, thanks! 🙌",
      ai4: "You're welcome! I've also notified clients about the therapist change. Everything is in sync! 💆",
      reassignDone: "4 treatments reassigned · 4 clients notified",
      statsBookings: "Today's bookings",
      statsUtilization: "Utilization",
      statsRevenue: "Weekly revenue",
      staff: [
        { name: "Kis Anna", role: "Senior therapist", status: "Available" },
        { name: "Nagy Bence", role: "Sports massage", status: "In session" },
        { name: "Varga Lili", role: "Relaxation therapist", status: "Sick" },
        { name: "Fehér Márk", role: "Thai massage", status: "Available" },
      ],
    },
    heroDescription: "Build your website, manage your calendar and marketing through chat - AI understands everything and helps you. For solo massage therapists and multi-therapist wellness centers.",
    heroImageAlt: "Massage therapist working with client",
    soloBadge: "For Solo Massage Therapists",
    soloTitle: "Perfect for solo massage therapists",
    soloDescription: "If you work alone, Bespire AI is your perfect assistant. It handles appointments, clients, marketing - so you can focus on what you love.",
    teamBadge: "For Multi-Therapist Wellness Centers",
    teamTitle: "Complex wellness center management made simple",
    teamDescription: "If multiple therapists work in the center, Bespire helps coordinate the team.",
    teamImageAlt: "Wellness team collaboration",
    phoneDemo: {
      businessName: "Zen Wellness Spa",
      domain: "zenwellness.com",
      serviceName: "Relaxation massage",
      duration: "60 min",
      price: "$75",
      appointment: "Saturday, 11:00",
      therapist: "Anna",
      appointmentLabel: "Appointment",
      therapistLabel: "Therapist",
      confirmButton: "Confirm booking",
      url: "zenwellness.com/booking",
      tagline: "Professional massage treatments",
      selectService: "Select a service",
      services: [
        { name: "Swedish massage", duration: "60 min", price: "$75" },
        { name: "Thai massage", duration: "90 min", price: "$95" },
        { name: "Sports massage", duration: "45 min", price: "$60" },
      ],
      selectTime: "Select a time",
      timeSlots: ["09:00", "10:30", "12:00", "14:00", "16:00", "18:00"],
      confirmed: "Booking confirmed!",
      confirmedSubtext: "We'll send you an SMS reminder",
      browserLabel: "From browser, no app needed",
      noAppNeeded: "No app download needed!",
    },
    aiChatExamples: {
      website: {
        userMessage: "Add a section with massage types and prices",
        aiResponse: "Done! I've created a section for massage types. Swedish, Thai, sports massage - each with its own description. Would you like to modify it?",
        aiAction: "AI creates the massage types section",
      },
      calendar: {
        userMessage: "What are my available slots tomorrow afternoon for Swedish massage?",
        aiResponse: "Tomorrow afternoon you have slots at 2:00 PM, 4:00 PM, and 5:30 PM for a 60-minute Swedish massage. Should I book someone?",
        aiAction: "AI instantly shows available slots",
      },
      marketing: {
        userMessage: "Create an email campaign for those who haven't visited me in 2 months, offer them 20% discount",
        aiResponse: "Found 31 inactive clients. Email ready with 20% discount coupon. Send now or schedule for tomorrow at 9 AM?",
        aiAction: "AI creates and targets the campaign",
      },
    },
    sellProducts: {
      title: "What can you sell as a massage therapist?",
      description: "Multiple revenue streams, one platform - with Bespire you can sell not just massages, but digital products, courses, and consulting too",
      products: [
        {
          emoji: "💆",
          title: "Massage Treatments",
          description: "Your foundation - clients can book appointments online for all your treatments",
          items: [
            "Swedish relaxation massage",
            "Sports massage, trigger point therapy",
            "Thai massage, stretching",
            "Couples massage experience",
          ],
        },
        {
          emoji: "💬",
          title: "Posture Consulting",
          description: "Higher price category - online or in-person expert consultation",
          items: [
            "Posture analysis and correction",
            "Pain management strategy",
            "Ergonomic consulting for office workers",
            "Home stretching routine design",
          ],
        },
        {
          emoji: "👥",
          title: "Wellness Workshops",
          description: "Reach more people at once - higher revenue, community building",
          items: [
            "Couples massage course",
            "Self-massage techniques workshop",
            "Stress management and relaxation",
            "Wellness weekend program",
          ],
        },
        {
          emoji: "📚",
          title: "Digital Products",
          description: "Passive income - create once, sell continuously",
          items: [
            "Self-massage guide PDF",
            "Stretching routine video pack",
            "Relaxation techniques e-book",
            "Home wellness tips",
          ],
        },
        {
          emoji: "🎫",
          title: "Passes and Packages",
          description: "Stable, predictable revenue - loyal clients guaranteed",
          items: [
            "10-session massage pass",
            "Monthly wellness package",
            "VIP annual membership",
            "Gift vouchers",
          ],
        },
      ],
    },
  },
  hairdressers: {
    slug: "hairdresser",
    heroTitle: "AI assistant that helps your daily work",
    heroAnimation: {
      chat: {
        header: "Bespire AI Assistant",
        subheader: "Online · Always available",
        inputPlaceholder: "Type a message...",
        user1: "Build me a website with services and pricing!",
        ai1: "Done! I've built your site with 3 sections: hero, services and pricing. Here's your URL: elegancehair.bespire.com",
        user2: "Book Kovács Éva for coloring tomorrow at 2 PM!",
        ai2: "Booked! Kovács Éva - Color and cut, tomorrow 2:00 PM. I've sent her a notification too.",
        user3: "Send an email to clients who haven't visited in 2 months!",
        ai3: "Campaign created and sent to 45 inactive clients. Already at 78% open rate!",
        user4: "Thanks, this is fantastic! 😍",
        ai4: "You're welcome! If you need anything, just ask. I can help with everything: website, calendar, clients, marketing - you just create! ✂️",
      },
    },
    websiteAnimation: {
      chatHeader: "Website Builder",
      chatSubheader: "AI assistant",
      domain: "elegancehair.bespire.com",
      chatPrompt: "Build my page with services and pricing!",
      aiMsg1: "Sure! I've started building your site. Just a few seconds and it'll be ready...",
      aiMsg2: "Sections are done:",
      doneLabel: "Done! Your site is live and available:",
      userFollowUp: "Can you change the colors to pink and black?",
      aiFollowUpReply: "Done! I've switched the colors to pink and black. Check it out! 🎨",
      blocks: {
        hero: "Hero section",
        services: "Services",
        pricing: "Pricing",
      },
    },
    calendarAnimation: {
      chatHeader: "Calendar Management",
      month: "March 2026",
      subtitle: "AI assistant",
      synced: "Synced",
      freeLabel: "Free",
      userAsk: "What are my free slots tomorrow?",
      aiResponse: "Your tomorrow's schedule:",
      userBook: "Book the 2:00 PM slot for Kovács Éva for coloring!",
      aiConfirm: "Done! 2:00 PM booked for Kovács Éva",
      notifSent: "Notification sent to client",
      userThanks: "Thanks, great! 👍",
      days: ["M", "T", "W", "Th", "F", "Sa", "Su"],
      slots: {
        slot1: "Nagy A. - Color",
        slot2: "Tóth M. - Haircut",
        slot3: "Szabó K. - Highlights",
        slot4: "Kiss R. - Special occasion",
        slot5: "Varga L. - Treatment",
      },
      newBookingNotif: "New booking: Kovács É. - Color 2:00 PM",
    },
    clientsAnimation: {
      chatHeader: "Client Management",
      chatSubheader: "AI assistant",
      userAsk: "Show me Nagy Anna's profile!",
      userAction: "Send her a reminder about her next coloring appointment!",
      aiConfirm: "Done! Reminder sent to Nagy Anna",
      visitsLabel: "visits",
      clients: {
        client1: { name: "Nagy Anna", tag: "VIP", status: "Regular client" },
        client2: { name: "Kovács Éva", tag: "New", status: "First visit" },
        client3: { name: "Szabó Kata", tag: "VIP", status: "Monthly pass" },
        client4: { name: "Kiss Réka", tag: "Returning", status: "10-session" },
      },
      tabs: {
        profile: "Profile",
        history: "History",
        notes: "Notes",
      },
      timeline: {
        firstVisit: "First visit",
        passBought: "5-session pass",
        session: "Color + cut #4",
        renewal: "Pass renewal",
      },
      stats: {
        retention: "Retention",
        vipClients: "VIP clients",
        growth: "Growth",
      },
    },
    teamAnimation: {
      chatHeader: "Salon Management",
      chatSubheader: "AI assistant · Online",
      inputPlaceholder: "Type a message...",
      user1: "Show me today's team! Who's working?",
      ai1: "Here's today's salon status:",
      user2: "How are today's bookings looking?",
      ai2: "Here are today's stats:",
      user3: "Lakatos Petra is sick, redistribute her appointments to the others!",
      ai3: "I've reassigned Lakatos Petra's 6 appointments to available stylists.",
      user4: "Great, thanks! 🙌",
      ai4: "You're welcome! I've also notified clients about the stylist change. Everything is in sync! ✂️",
      reassignDone: "6 appointments reassigned · 4 clients notified",
      statsBookings: "Today's bookings",
      statsUtilization: "Utilization",
      statsRevenue: "Weekly revenue",
      staff: [
        { name: "Molnár Kata", role: "Senior stylist", status: "Available" },
        { name: "Tóth Bence", role: "Barber", status: "With client" },
        { name: "Lakatos Petra", role: "Colorist", status: "Sick" },
        { name: "Kiss Dóra", role: "Junior stylist", status: "Available" },
      ],
    },
    heroDescription: "Build your website, manage your calendar and marketing through chat - AI understands everything and helps you. For solo hairdressers and multi-stylist salons.",
    heroImageAlt: "Hairdresser working with client",
    soloBadge: "For Solo Hairdressers",
    soloTitle: "Perfect for solo hairdressers & barbers",
    soloDescription: "If you work alone, Bespire AI is your perfect assistant. It handles appointments, clients, marketing - so you can focus on what you love.",
    teamBadge: "For Multi-Stylist Salons",
    teamTitle: "Complex salon management made simple",
    teamDescription: "If multiple stylists work in your salon, Bespire helps coordinate the team.",
    teamImageAlt: "Salon team collaboration",
    phoneDemo: {
      businessName: "Elegance Hair Studio",
      domain: "elegancehair.com",
      serviceName: "Color and cut",
      duration: "90 min",
      price: "$120",
      appointment: "Friday, 2:00 PM",
      therapist: "Kate",
      appointmentLabel: "Appointment",
      therapistLabel: "Stylist",
      confirmButton: "Confirm booking",
      url: "elegancehair.com/booking",
      tagline: "Professional hair styling services",
      selectService: "Select a service",
      services: [
        { name: "Color and cut", duration: "90 min", price: "$120" },
        { name: "Men's haircut", duration: "30 min", price: "$25" },
        { name: "Highlights", duration: "120 min", price: "$150" },
      ],
      selectTime: "Select a time",
      timeSlots: ["09:00", "10:30", "12:00", "14:00", "16:00", "18:00"],
      confirmed: "Booking confirmed!",
      confirmedSubtext: "We'll send you an SMS reminder",
      browserLabel: "From browser, no app needed",
      noAppNeeded: "No app download needed!",
    },
    aiChatExamples: {
      website: {
        userMessage: "Add a section with haircut types and prices",
        aiResponse: "Done! I've created a section for services. Haircut, color, highlights - each with its own description. Would you like to modify it?",
        aiAction: "AI creates the services section",
      },
      calendar: {
        userMessage: "What are my available slots tomorrow afternoon for coloring?",
        aiResponse: "Tomorrow afternoon you have slots at 1:00 PM, 3:30 PM, and 5:00 PM for a 90-minute coloring. Should I book someone?",
        aiAction: "AI instantly shows available slots",
      },
      marketing: {
        userMessage: "Create an email campaign for those who haven't visited me in 2 months, offer them 20% discount",
        aiResponse: "Found 45 inactive clients. Email ready with 20% discount coupon. Send now or schedule for tomorrow at 9 AM?",
        aiAction: "AI creates and targets the campaign",
      },
    },
    sellProducts: {
      title: "What can you sell as a hairdresser?",
      description: "Multiple revenue streams, one platform - with Bespire you can sell not just haircuts, but digital products, courses, and consulting too",
      products: [
        {
          emoji: "✂️",
          title: "Hair Services",
          description: "Your foundation - clients can book appointments online for all your services",
          items: [
            "Haircut, styling",
            "Color, highlights, balayage",
            "Special occasion styling",
            "Treatments and care",
          ],
        },
        {
          emoji: "💇",
          title: "Styling Consulting",
          description: "Higher price category - personalized style consultation",
          items: [
            "Face shape hair advice",
            "Home hair care consultation",
            "Color consulting",
            "Bridal style planning",
          ],
        },
        {
          emoji: "👥",
          title: "Workshops",
          description: "Reach more people at once - higher revenue, community building",
          items: [
            "Home styling course",
            "Braiding techniques workshop",
            "Hair care for beginners",
            "Styling masterclass",
          ],
        },
        {
          emoji: "📚",
          title: "Digital Products",
          description: "Passive income - create once, sell continuously",
          items: [
            "Hair care guide PDF",
            "Home styling video pack",
            "Hairstyle inspiration e-book",
            "Product recommendations catalog",
          ],
        },
        {
          emoji: "🎫",
          title: "Passes and Packages",
          description: "Stable, predictable revenue - loyal clients guaranteed",
          items: [
            "5-haircut pass",
            "Monthly styling package",
            "VIP membership with extra discounts",
            "Gift vouchers",
          ],
        },
      ],
    },
  },
  fitnessTrainers: {
    slug: "fitness-trainer",
    heroTitle: "AI assistant that helps your daily work",
    heroAnimation: {
      chat: {
        header: "Bespire AI Assistant",
        subheader: "Online · Always available",
        inputPlaceholder: "Type a message...",
        user1: "Build me a website with workouts and pricing!",
        ai1: "Done! I've built your site with 3 sections: hero, workouts and pricing. Here's your URL: fitpro-anna.bespire.com",
        user2: "Book Szabó Dávid for personal training tomorrow at 9 AM!",
        ai2: "Booked! Szabó Dávid - Personal training, tomorrow 9:00 AM. I've sent him a notification too.",
        user3: "Send an email to clients who haven't visited in 2 months!",
        ai3: "Campaign created and sent to 28 inactive clients. Already at 82% open rate!",
        user4: "Thanks, this is fantastic! 😍",
        ai4: "You're welcome! If you need anything, just ask. I can help with everything: website, calendar, clients, marketing - you just train! 💪",
      },
    },
    websiteAnimation: {
      chatHeader: "Website Builder",
      chatSubheader: "AI assistant",
      domain: "fitpro-anna.bespire.com",
      chatPrompt: "Build my page with workouts and pricing!",
      aiMsg1: "Sure! I've started building your site. Just a few seconds and it'll be ready...",
      aiMsg2: "Sections are done:",
      doneLabel: "Done! Your site is live and available:",
      userFollowUp: "Can you change the colors to green and black?",
      aiFollowUpReply: "Done! I've switched the colors to green and black. Check it out! 🎨",
      blocks: {
        hero: "Hero section",
        services: "Services",
        pricing: "Pricing",
      },
    },
    calendarAnimation: {
      chatHeader: "Calendar Management",
      month: "March 2026",
      subtitle: "AI assistant",
      synced: "Synced",
      freeLabel: "Free",
      userAsk: "What are my free slots tomorrow?",
      aiResponse: "Your tomorrow's schedule:",
      userBook: "Book the 9:00 slot for Szabó Dávid!",
      aiConfirm: "Done! 09:00 booked for Szabó Dávid",
      notifSent: "Notification sent to client",
      userThanks: "Thanks, great! 👍",
      days: ["M", "T", "W", "Th", "F", "Sa", "Su"],
      slots: {
        slot1: "Kiss R. - Personal",
        slot2: "Crossfit group (8 ppl)",
        slot3: "Szabó D. - Conditioning",
        slot4: "TRX advanced (12 ppl)",
        slot5: "HIIT beginner (6 ppl)",
      },
      newBookingNotif: "New booking: Kovács B. - Personal 4:00 PM",
    },
    clientsAnimation: {
      chatHeader: "Client Management",
      chatSubheader: "AI assistant",
      userAsk: "Show me Kiss Réka's profile!",
      userAction: "Send her a reminder about her expiring pass!",
      aiConfirm: "Done! Reminder sent to Kiss Réka",
      visitsLabel: "visits",
      clients: {
        client1: { name: "Kiss Réka", tag: "VIP", status: "Active pass" },
        client2: { name: "Szabó Dávid", tag: "New", status: "Trial session" },
        client3: { name: "Nagy Anna", tag: "VIP", status: "10-session" },
        client4: { name: "Tóth Péter", tag: "Returning", status: "Monthly pass" },
      },
      tabs: {
        profile: "Profile",
        history: "History",
        notes: "Notes",
      },
      timeline: {
        firstVisit: "First visit",
        passBought: "10-session pass",
        session: "Personal training #8",
        renewal: "Pass renewal",
      },
      stats: {
        retention: "Retention",
        vipClients: "VIP clients",
        growth: "Growth",
      },
    },
    marketingAnimation: {
      chatHeader: "Marketing",
      title: "Marketing automation",
      subtitle: "AI assistant",
      userAsk: "Bring back clients inactive for 2 months!",
      aiCampaignIntro: "I've created the campaign! Here's the email preview:",
      emailSubject: "We miss you!",
      emailPreview: "Free comeback training coupon",
      emailCta: "Book now!",
      userSend: "Send it now! 🚀",
      aiSentConfirm: "Sent to all 28 contacts!",
      userReply: "This is fantastic! 😍",
      sentLabel: "Sent",
      opensLabel: "Opens",
      funnel: {
        audience: "Audience",
        audienceDetail: "28 inactive clients",
        content: "Content",
        contentDetail: "Comeback offer email",
        sending: "Sending",
        sendingDetail: "Sent to 28 contacts",
        results: "Results",
        resultsDetail: "82% open rate",
        resultTitle: "Campaign results",
        live: "● Live",
        conversions: "Returning",
      },
    },
    teamAnimation: {
      chatHeader: "Team Management",
      chatSubheader: "AI assistant · Online",
      inputPlaceholder: "Type a message...",
      user1: "Show me today's team! Who's working?",
      ai1: "Here's today's team status:",
      user2: "How are today's bookings looking?",
      ai2: "Here are today's stats:",
      user3: "Tóth Kata is sick, redistribute her classes to the others!",
      ai3: "I've reassigned Tóth Kata's 5 classes to available trainers.",
      user4: "Great, thanks! 🙌",
      ai4: "You're welcome! I've also notified clients about the trainer change. Everything is in sync! 💪",
      reassignDone: "5 classes reassigned · 3 clients notified",
      statsBookings: "Today's bookings",
      statsUtilization: "Utilization",
      statsRevenue: "Weekly revenue",
      staff: [
        { name: "Kovács Anna", role: "Senior trainer", status: "Available" },
        { name: "Nagy Bence", role: "Personal trainer", status: "In session" },
        { name: "Tóth Kata", role: "Group trainer", status: "Sick" },
        { name: "Kiss Márk", role: "Fitness trainer", status: "Available" },
      ],
    },
    heroDescription: "Build your website, manage your calendar and marketing through chat - AI understands everything and helps you. For solo trainers and multi-trainer fitness centers.",
    heroImageAlt: "Fitness trainer working with client",
    soloBadge: "For Solo Trainers",
    soloTitle: "Perfect for personal trainers",
    soloDescription: "If you work alone, Bespire AI is your perfect assistant. It handles appointments, clients, marketing - so you can focus on what you love.",
    teamBadge: "For Multi-Trainer Fitness Centers",
    teamTitle: "Complex fitness center management made simple",
    teamDescription: "If multiple trainers work in the gym, Bespire helps coordinate the team.",
    teamImageAlt: "Fitness team collaboration",
    phoneDemo: {
      businessName: "FitPro Studio",
      domain: "fitpro.com",
      serviceName: "Personal training",
      duration: "60 min",
      price: "$60",
      appointment: "Monday, 8:00 AM",
      therapist: "Mark",
      appointmentLabel: "Appointment",
      therapistLabel: "Trainer",
      confirmButton: "Confirm booking",
      url: "fitpro.com/booking",
      tagline: "Personal training & group classes",
      selectService: "Select a service",
      services: [
        { name: "Personal Training", duration: "60 min", price: "$80" },
        { name: "Group Class", duration: "45 min", price: "$25" },
        { name: "Online Coaching", duration: "30 min", price: "$50" },
      ],
      selectTime: "Select a time",
      timeSlots: ["08:00", "09:30", "10:00", "11:30", "14:00", "16:00"],
      confirmed: "Booking confirmed!",
      confirmedSubtext: "We'll send you an SMS reminder",
      browserLabel: "From browser, no app needed",
      noAppNeeded: "No app download needed!",
    },
    aiChatExamples: {
      website: {
        userMessage: "Add a section with workout types and prices",
        aiResponse: "Done! I've created a section for workouts. Personal training, group class, online coaching - each with its own description. Would you like to modify it?",
        aiAction: "AI creates the workouts section",
      },
      calendar: {
        userMessage: "What are my available morning slots tomorrow for personal training?",
        aiResponse: "Tomorrow morning you have slots at 7:00 AM, 8:30 AM, and 10:00 AM for a 60-minute personal training. Should I book someone?",
        aiAction: "AI instantly shows available slots",
      },
      marketing: {
        userMessage: "Create an email campaign for those who haven't trained with me in 2 months, offer them a free session",
        aiResponse: "Found 28 inactive clients. Email ready with free comeback session coupon. Send now or schedule for tomorrow at 9 AM?",
        aiAction: "AI creates and targets the campaign",
      },
    },
    sellProducts: {
      title: "What can you sell as a trainer?",
      description: "Multiple revenue streams, one platform - with Bespire you can sell not just workouts, but digital products, courses, and consulting too",
      products: [
        {
          emoji: "💪",
          title: "Workouts",
          description: "Your foundation - clients can book appointments online for all your sessions",
          items: [
            "Personal training",
            "Group functional training",
            "Online training via video call",
            "Outdoor training",
          ],
        },
        {
          emoji: "📋",
          title: "Training Plans",
          description: "Higher price category - personalized programs",
          items: [
            "Custom workout program design",
            "Nutrition + training plan package",
            "Competition prep program",
            "Rehabilitation training plan",
          ],
        },
        {
          emoji: "👥",
          title: "Group Programs",
          description: "Reach more people at once - higher revenue, community building",
          items: [
            "Bootcamp weekend",
            "8-week transformation challenge",
            "Summer fitness camp",
            "Group morning workouts",
          ],
        },
        {
          emoji: "📚",
          title: "Digital Products",
          description: "Passive income - create once, sell continuously",
          items: [
            "Home workout program PDF",
            "Workout video pack",
            "Nutrition guide",
            "Motivation e-book",
          ],
        },
        {
          emoji: "🎫",
          title: "Passes and Memberships",
          description: "Stable, predictable revenue - loyal clients guaranteed",
          items: [
            "10-session workout pass",
            "Monthly unlimited membership",
            "VIP annual program",
            "Gift vouchers",
          ],
        },
      ],
    },
  },
  yogaInstructors: {
    slug: "yoga-instructor",
    heroTitle: "AI assistant that helps your daily work",
    heroAnimation: {
      chat: {
        header: "Bespire AI Assistant",
        subheader: "Online · Always available",
        inputPlaceholder: "Type a message...",
        user1: "Build me a website with yoga classes and schedule!",
        ai1: "Done! I've built your site with 3 sections: hero, yoga classes and schedule. Here's your URL: harmonyyoga.bespire.com",
        user2: "Sign up Kovács Anna for tomorrow's 6 PM Hatha yoga class!",
        ai2: "Signed up! Kovács Anna - Hatha Yoga, tomorrow 6:00 PM. I've sent her a notification too. 🧘",
        user3: "Send an email to students who haven't attended in 2 months!",
        ai3: "Campaign created and sent to 19 inactive students. Already at 78% open rate! 🙏",
        user4: "Thanks, this is fantastic! 😍",
        ai4: "You're welcome! If you need anything, just ask. Website, calendar, students, marketing - you just teach! 🧘‍♀️",
      },
    },
    websiteAnimation: {
      chatHeader: "Website Builder",
      chatSubheader: "AI assistant",
      domain: "harmonyyoga.bespire.com",
      chatPrompt: "Build my page with yoga classes and schedule!",
      aiMsg1: "Sure! I've started building your site. Just a few seconds and it'll be ready...",
      aiMsg2: "Sections are done:",
      doneLabel: "Done! Your site is live and available:",
      userFollowUp: "Can you change the colors to purple and white?",
      aiFollowUpReply: "Done! I've switched the colors to purple and white. Looks beautiful! 🧘",
      blocks: {
        hero: "Hero section",
        services: "Yoga classes",
        pricing: "Schedule & pricing",
      },
    },
    calendarAnimation: {
      chatHeader: "Calendar Management",
      month: "March 2026",
      subtitle: "AI assistant",
      synced: "Synced",
      freeLabel: "Available",
      userAsk: "What are the available spots in tomorrow's classes?",
      aiResponse: "Tomorrow's classes and availability:",
      userBook: "Sign up Kovács Anna for the 6 PM Hatha class!",
      aiConfirm: "Done! Kovács Anna signed up for 6:00 PM Hatha yoga",
      notifSent: "Notification sent to student",
      userThanks: "Thanks, great! 🙏",
      days: ["M", "T", "W", "Th", "F", "Sa", "Su"],
      slots: {
        slot1: "Hatha yoga (8/12 ppl)",
        slot2: "Vinyasa flow (6/10 ppl)",
        slot3: "",
        slot4: "Yin yoga (10/10 ppl)",
        slot5: "",
      },
      newBookingNotif: "New signup: Tóth B. - Morning yoga 7:00 AM",
    },
    clientsAnimation: {
      chatHeader: "Student Management",
      chatSubheader: "AI assistant",
      userAsk: "Show me Kovács Anna's profile!",
      userAction: "Send her a reminder about her expiring pass!",
      aiConfirm: "Done! Reminder sent to Kovács Anna",
      visitsLabel: "visits",
      clients: {
        client1: { name: "Kovács Anna", tag: "Regular", status: "Active pass" },
        client2: { name: "Nagy Lilla", tag: "New", status: "Trial class" },
        client3: { name: "Szabó Eszter", tag: "VIP", status: "Unlimited" },
        client4: { name: "Tóth Petra", tag: "Returning", status: "10-class" },
      },
      tabs: {
        profile: "Profile",
        history: "History",
        notes: "Notes",
      },
      timeline: {
        firstVisit: "First class",
        passBought: "10-class pass",
        session: "Hatha yoga #8",
        renewal: "Pass renewal",
      },
      stats: {
        retention: "Retention",
        vipClients: "Regulars",
        growth: "Growth",
      },
    },
    marketingAnimation: {
      chatHeader: "Marketing",
      title: "Marketing automation",
      subtitle: "AI assistant",
      userAsk: "Bring back students inactive for 2 months!",
      aiCampaignIntro: "I've created the campaign! Here's the email preview:",
      emailSubject: "We miss you at class! 🧘",
      emailPreview: "Free comeback yoga class coupon",
      emailCta: "Sign up!",
      userSend: "Send it now! 🚀",
      aiSentConfirm: "Sent to all 19 contacts!",
      userReply: "This is fantastic! 😍",
      sentLabel: "Sent",
      opensLabel: "Opens",
      funnel: {
        audience: "Audience",
        audienceDetail: "19 inactive students",
        content: "Content",
        contentDetail: "Comeback offer email",
        sending: "Sending",
        sendingDetail: "Sent to 19 contacts",
        results: "Results",
        resultsDetail: "78% open rate",
        resultTitle: "Campaign results",
        live: "● Live",
        conversions: "Returning",
      },
    },
    teamAnimation: {
      chatHeader: "Team Management",
      chatSubheader: "AI assistant · Online",
      inputPlaceholder: "Type a message...",
      user1: "Show me today's instructors! Who's teaching?",
      ai1: "Here's today's instructor status:",
      user2: "How are today's signups looking?",
      ai2: "Here are today's stats:",
      user3: "Tóth Lilla is sick, redistribute her classes to the others!",
      ai3: "I've reassigned Tóth Lilla's 4 classes to available instructors.",
      user4: "Great, thanks! 🙏",
      ai4: "You're welcome! I've also notified students about the instructor change. Everything is in sync! 🧘",
      reassignDone: "4 classes reassigned · 12 students notified",
      statsBookings: "Today's signups",
      statsUtilization: "Utilization",
      statsRevenue: "Weekly revenue",
      staff: [
        { name: "Kovács Lilla", role: "Hatha instructor", status: "Available" },
        { name: "Nagy Eszter", role: "Vinyasa instructor", status: "In class" },
        { name: "Tóth Lilla", role: "Yin instructor", status: "Sick" },
        { name: "Kiss Réka", role: "Morning yoga", status: "Available" },
      ],
    },
    heroDescription: "Build your website, manage your calendar and marketing through chat - AI understands everything and helps you. For solo yoga instructors and multi-instructor studios.",
    heroImageAlt: "Yoga instructor teaching class",
    soloBadge: "For Solo Yoga Instructors",
    soloTitle: "Perfect for solo yoga instructors",
    soloDescription: "If you work alone, Bespire AI is your perfect assistant. It handles classes, students, marketing - so you can focus on what you love.",
    teamBadge: "For Multi-Instructor Studios",
    teamTitle: "Complex studio management made simple",
    teamDescription: "If multiple instructors work in the studio, Bespire helps coordinate the team.",
    teamImageAlt: "Yoga studio team",
    phoneDemo: {
      businessName: "Harmony Yoga Studio",
      domain: "harmonyyoga.com",
      serviceName: "Hatha Yoga",
      duration: "75 min",
      price: "$20",
      appointment: "Wednesday, 6:00 PM",
      therapist: "Lily",
      appointmentLabel: "Appointment",
      therapistLabel: "Instructor",
      confirmButton: "Confirm registration",
      url: "harmonyyoga.com/booking",
      tagline: "Yoga & Pilates classes for all levels",
      selectService: "Select a class",
      services: [
        { name: "Hatha Yoga", duration: "75 min", price: "$20" },
        { name: "Vinyasa Flow", duration: "60 min", price: "$22" },
        { name: "Yin Yoga", duration: "90 min", price: "$25" },
      ],
      selectTime: "Select a time",
      timeSlots: ["07:00", "09:00", "10:30", "17:00", "18:30", "20:00"],
      confirmed: "Registration confirmed!",
      confirmedSubtext: "We'll send you an SMS reminder",
      browserLabel: "From browser, no app needed",
      noAppNeeded: "No app download needed!",
    },
    aiChatExamples: {
      website: {
        userMessage: "Add a section with yoga class types and schedule",
        aiResponse: "Done! I've created a section for classes. Hatha, Vinyasa, Yin yoga - each with its own description and schedule. Would you like to modify it?",
        aiAction: "AI creates the yoga classes section",
      },
      calendar: {
        userMessage: "How many spots are available in tonight's Hatha yoga class?",
        aiResponse: "Tonight at 6:00 PM the Hatha yoga class has 4 available spots out of 12. Should I book someone?",
        aiAction: "AI instantly shows available spots",
      },
      marketing: {
        userMessage: "Create an email campaign for those who haven't attended class in 2 months, offer them a free class",
        aiResponse: "Found 19 inactive students. Email ready with free comeback class coupon. Send now or schedule for tomorrow at 9 AM?",
        aiAction: "AI creates and targets the campaign",
      },
    },
    sellProducts: {
      title: "What can you sell as a yoga instructor?",
      description: "Multiple revenue streams, one platform - with Bespire you can sell not just classes, but digital products, courses, and consulting too",
      products: [
        {
          emoji: "🧘",
          title: "Yoga Classes",
          description: "Your foundation - students can register online for all your classes",
          items: [
            "Hatha yoga for beginners",
            "Vinyasa flow for advanced",
            "Yin yoga and meditation",
            "Morning wake-up yoga",
          ],
        },
        {
          emoji: "🙏",
          title: "Private Consultation",
          description: "Higher price category - personalized yoga guidance",
          items: [
            "Custom yoga practice design",
            "Meditation techniques teaching",
            "Breathwork and stress management",
            "Yoga lifestyle consulting",
          ],
        },
        {
          emoji: "👥",
          title: "Workshops and Retreats",
          description: "Deeper experiences - higher revenue, community building",
          items: [
            "Weekend yoga retreat",
            "Meditation workshop",
            "Chakra workshop series",
            "Summer yoga camp",
          ],
        },
        {
          emoji: "📚",
          title: "Digital Products",
          description: "Passive income - create once, sell continuously",
          items: [
            "Home yoga routine PDF",
            "Meditation audio pack",
            "Yoga video series",
            "Breathing exercises guide",
          ],
        },
        {
          emoji: "🎫",
          title: "Passes and Memberships",
          description: "Stable, predictable revenue - loyal students guaranteed",
          items: [
            "10-class yoga pass",
            "Monthly unlimited membership",
            "VIP annual program",
            "Gift vouchers",
          ],
        },
      ],
    },
  },
  beauticians: {
    slug: "beautician",
    heroTitle: "AI assistant that helps your daily work",
    heroAnimation: {
      chat: {
        header: "Bespire AI Assistant",
        subheader: "Online · Always available",
        inputPlaceholder: "Type a message...",
        user1: "Build me a website with treatments and prices!",
        ai1: "Done! I've built your site with 3 sections: hero, treatments, and pricing. Here's your link: glowbeauty.bespire.app",
        user2: "Book Anna Kovacs for hydrafacial tomorrow at 10:00!",
        ai2: "Booked! Anna Kovacs - Hydrafacial, tomorrow 10:00. Notification sent! ✨",
        user3: "Email everyone who hasn't visited in 3 months!",
        ai3: "Campaign ready and sent to 38 inactive clients. Already at 88% open rate!",
        user4: "Thanks, this is amazing! 😍",
        ai4: "You're welcome! Anything you need - website, calendar, clients, marketing - just focus on making the world beautiful! 💄",
      },
    },
    websiteAnimation: {
      chatHeader: "Website Builder",
      chatSubheader: "AI assistant",
      domain: "glowbeauty.bespire.app",
      chatPrompt: "Build my website with treatments and pricing!",
      aiMsg1: "Sure! I've started building your site. Just a few seconds...",
      aiMsg2: "Sections ready:",
      doneLabel: "Done! Your site is live at:",
      userFollowUp: "Can you change the colors to rose and gold?",
      aiFollowUpReply: "Done! Switched to rose and gold. Gorgeous! 🌸",
      blocks: {
        hero: "Hero section",
        services: "Treatments",
        pricing: "Price list",
      },
    },
    calendarAnimation: {
      chatHeader: "Calendar Management",
      month: "March 2026",
      subtitle: "AI assistant",
      synced: "Synced",
      freeLabel: "Available",
      userAsk: "What are my free slots tomorrow?",
      aiResponse: "Your tomorrow's slots:",
      userBook: "Book the 10:00 slot for Anna Kovacs - hydrafacial!",
      aiConfirm: "Done! 10:00 booked for Anna Kovacs - Hydrafacial",
      notifSent: "Notification sent to client",
      userThanks: "Thanks, awesome! ✨",
      days: ["M", "T", "W", "T", "F", "S", "S"],
      slots: {
        slot1: "E. Nagy - Facial",
        slot2: "K. Szabo - Mesotherapy",
        slot3: "",
        slot4: "R. Kiss - Anti-aging",
        slot5: "",
      },
      newBookingNotif: "New booking: A. Toth - Hydrafacial 2:00 PM",
    },
    clientsAnimation: {
      chatHeader: "Client Management",
      chatSubheader: "AI assistant",
      userAsk: "Show me Anna Kiss's profile!",
      userAction: "Send her a reminder about her expiring package!",
      aiConfirm: "Done! Reminder sent to Anna Kiss",
      visitsLabel: "treatments",
      clients: {
        client1: { name: "Anna Kiss", tag: "VIP", status: "Active package" },
        client2: { name: "Kate Szabo", tag: "New", status: "First treatment" },
        client3: { name: "Reka Nagy", tag: "VIP", status: "Annual membership" },
        client4: { name: "Sophie Toth", tag: "Returning", status: "Monthly program" },
      },
      tabs: {
        profile: "Profile",
        history: "History",
        notes: "Notes",
      },
      timeline: {
        firstVisit: "First treatment",
        passBought: "5-treatment package",
        session: "Hydrafacial #4",
        renewal: "Package renewal",
      },
      stats: {
        retention: "Retention",
        vipClients: "VIP clients",
        growth: "Growth",
      },
    },
    marketingAnimation: {
      chatHeader: "Marketing",
      title: "Marketing automation",
      subtitle: "AI assistant",
      userAsk: "Win back clients inactive for 3 months!",
      aiCampaignIntro: "Campaign ready! Here's the email preview:",
      emailSubject: "We miss you! ✨",
      emailPreview: "25% off your first returning treatment",
      emailCta: "Book now!",
      userSend: "Send it now! 🚀",
      aiSentConfirm: "Sent to all 38 addresses!",
      userReply: "This is amazing! 😍",
      sentLabel: "Sent",
      opensLabel: "Opens",
      funnel: {
        audience: "Audience",
        audienceDetail: "38 inactive clients",
        content: "Content",
        contentDetail: "Returning discount email",
        sending: "Sending",
        sendingDetail: "Sent to 38 addresses",
        results: "Results",
        resultsDetail: "88% open rate",
        resultTitle: "Campaign result",
        live: "● Live",
        conversions: "Returning",
      },
    },
    teamAnimation: {
      chatHeader: "Team Management",
      chatSubheader: "AI assistant · Online",
      inputPlaceholder: "Type a message...",
      user1: "Show today's team! Who's working?",
      ai1: "Here's the team status:",
      user2: "How are we doing with today's bookings?",
      ai2: "Here are today's stats:",
      user3: "Sophie Nagy called in sick, reassign her treatments!",
      ai3: "Reassigned Sophie Nagy's 4 treatments to available beauticians.",
      user4: "Great, thanks! 🙌",
      ai4: "You're welcome! I've also notified clients about the beautician change. Everything's in sync! ✨",
      reassignDone: "4 treatments reassigned · 4 clients notified",
      statsBookings: "Today's bookings",
      statsUtilization: "Utilization",
      statsRevenue: "Weekly revenue",
      staff: [
        { name: "Sophie Kovacs", role: "Senior beautician", status: "Available" },
        { name: "Anna Nagy", role: "Hydrafacial specialist", status: "In treatment" },
        { name: "Reka Kiss", role: "Anti-aging beautician", status: "Sick" },
        { name: "Lili Toth", role: "Permanent makeup", status: "Available" },
      ],
    },
    heroDescription: "Build your website, manage your calendar and marketing through chat - AI understands everything and helps you. For solo beauticians and multi-specialist salons.",
    heroImageAlt: "Beautician performing treatment",
    soloBadge: "For Solo Beauticians",
    soloTitle: "Perfect for solo beauticians",
    soloDescription: "If you work alone, Bespire AI is your perfect assistant. It handles appointments, clients, marketing - so you can focus on what you love.",
    teamBadge: "For Multi-Specialist Salons",
    teamTitle: "Complex beauty salon management made simple",
    teamDescription: "If multiple specialists work in the salon, Bespire helps coordinate the team.",
    teamImageAlt: "Beauty salon team collaboration",
    phoneDemo: {
      businessName: "Glow Beauty Salon",
      domain: "glowbeauty.com",
      serviceName: "Hydrafacial treatment",
      duration: "60 min",
      price: "$90",
      appointment: "Thursday, 10:00 AM",
      therapist: "Sophie",
      appointmentLabel: "Appointment",
      therapistLabel: "Beautician",
      confirmButton: "Confirm booking",
      url: "glowbeauty.com/booking",
      tagline: "Professional beauty treatments",
      selectService: "Select a treatment",
      services: [
        { name: "Hydrafacial", duration: "60 min", price: "$90" },
        { name: "Deep cleansing facial", duration: "45 min", price: "$65" },
        { name: "Anti-aging treatment", duration: "90 min", price: "$120" },
      ],
      selectTime: "Select a time",
      timeSlots: ["09:00", "10:30", "12:00", "14:00", "16:00", "18:00"],
      confirmed: "Booking confirmed!",
      confirmedSubtext: "We'll send you an SMS reminder",
      browserLabel: "From browser, no app needed",
      noAppNeeded: "No app download needed!",
    },
    aiChatExamples: {
      website: {
        userMessage: "Add a section with treatment types and prices",
        aiResponse: "Done! I've created a section for treatments. Facial cleansing, hydrafacial, anti-aging - each with its own description. Would you like to modify it?",
        aiAction: "AI creates the treatments section",
      },
      calendar: {
        userMessage: "What are my available slots tomorrow for hydrafacial?",
        aiResponse: "Tomorrow you have slots at 9:00 AM, 11:30 AM, and 2:00 PM for a 60-minute hydrafacial treatment. Should I book someone?",
        aiAction: "AI instantly shows available slots",
      },
      marketing: {
        userMessage: "Create an email campaign for those who haven't visited me in 3 months, offer them 25% discount on their first treatment",
        aiResponse: "Found 38 inactive clients. Email ready with 25% discount coupon. Send now or schedule for tomorrow at 9 AM?",
        aiAction: "AI creates and targets the campaign",
      },
    },
    sellProducts: {
      title: "What can you sell as a beautician?",
      description: "Multiple revenue streams, one platform - with Bespire you can sell not just treatments, but digital products, courses, and consulting too",
      products: [
        {
          emoji: "✨",
          title: "Beauty Treatments",
          description: "Your foundation - clients can book appointments online for all your treatments",
          items: [
            "Facial cleansing, hydration",
            "Hydrafacial, mesotherapy",
            "Anti-aging treatments",
            "Medical aesthetics",
          ],
        },
        {
          emoji: "💄",
          title: "Skincare Consulting",
          description: "Higher price category - personalized skincare routine",
          items: [
            "Skin type analysis and advice",
            "Home care routine design",
            "Product recommendations",
            "Seasonal skincare plan",
          ],
        },
        {
          emoji: "👥",
          title: "Beauty Workshops",
          description: "Reach more people at once - higher revenue, community building",
          items: [
            "Makeup masterclass",
            "Home care workshop",
            "Bachelorette beauty day",
            "Seasonal skincare course",
          ],
        },
        {
          emoji: "📚",
          title: "Digital Products",
          description: "Passive income - create once, sell continuously",
          items: [
            "Skincare guide PDF",
            "Makeup video pack",
            "Home SPA routine e-book",
            "Product reviews catalog",
          ],
        },
        {
          emoji: "🎫",
          title: "Treatment Packages",
          description: "Stable, predictable revenue - loyal clients guaranteed",
          items: [
            "5-treatment package",
            "Monthly beauty program",
            "VIP annual membership",
            "Gift vouchers",
          ],
        },
      ],
    },
  },
  consultants: {
    slug: "consultant",
    heroTitle: "AI assistant that helps your daily work",
    heroDescription: "Build your website, manage your calendar and marketing through chat - AI understands everything and helps you. For solo consultants and multi-consultant firms.",
    heroImageAlt: "Consultant in consultation",
    soloBadge: "For Solo Consultants",
    soloTitle: "Perfect for solo consultants",
    soloDescription: "If you work alone, Bespire AI is your perfect assistant. It handles appointments, clients, marketing - so you can focus on what you love.",
    teamBadge: "For Multi-Consultant Firms",
    teamTitle: "Complex consulting firm management made simple",
    teamDescription: "If multiple consultants work in the team, Bespire helps coordinate the work.",
    teamImageAlt: "Consulting team collaboration",
    phoneDemo: {
      businessName: "Growth Consulting",
      domain: "growthconsulting.com",
      serviceName: "Strategic consultation",
      duration: "90 min",
      price: "$250",
      appointment: "Tuesday, 10:00 AM",
      therapist: "Brian",
      appointmentLabel: "Appointment",
      therapistLabel: "Consultant",
      confirmButton: "Confirm booking",
      url: "growthconsulting.com/booking",
      tagline: "Business consulting & coaching",
      selectService: "Select a service",
      services: [
        { name: "Strategic consultation", duration: "90 min", price: "$250" },
        { name: "Business coaching", duration: "60 min", price: "$180" },
        { name: "Workshop", duration: "3 hours", price: "$500" },
      ],
      selectTime: "Select a time",
      timeSlots: ["09:00", "10:30", "13:00", "14:30", "16:00", "17:30"],
      confirmed: "Booking confirmed!",
      confirmedSubtext: "We'll send you an email reminder",
      browserLabel: "From browser, no app needed",
      noAppNeeded: "No app download needed!",
    },
    aiChatExamples: {
      website: {
        userMessage: "Add a section with my services and prices",
        aiResponse: "Done! I've created a section for services. Strategic consultation, coaching package, workshop - each with its own description. Would you like to modify it?",
        aiAction: "AI creates the services section",
      },
      calendar: {
        userMessage: "What are my available slots next week for strategic consultation?",
        aiResponse: "Next week you have slots on Tuesday at 10:00 AM, Wednesday at 2:00 PM, and Friday at 9:00 AM for a 90-minute strategic consultation. Should I book someone?",
        aiAction: "AI instantly shows available slots",
      },
      marketing: {
        userMessage: "Create an email campaign for those who inquired but didn't book, offer them a free 30-minute intro call",
        aiResponse: "Found 24 potential clients. Email ready with free intro call offer. Send now or schedule for tomorrow at 9 AM?",
        aiAction: "AI creates and targets the campaign",
      },
    },
    sellProducts: {
      title: "What can you sell as a consultant?",
      description: "Multiple revenue streams, one platform - with Bespire you can sell not just consultations, but digital products, courses, and workshops too",
      products: [
        {
          emoji: "💼",
          title: "Consultations",
          description: "Your foundation - clients can book appointments online for all your consultations",
          items: [
            "Individual strategic consultation",
            "Business analysis and recommendations",
            "Problem-solving session",
            "Follow-up and support",
          ],
        },
        {
          emoji: "🎯",
          title: "Coaching Programs",
          description: "Higher price category - long-term collaboration",
          items: [
            "3-month coaching package",
            "Annual mentoring program",
            "VIP intensive weekend",
            "Mastermind group",
          ],
        },
        {
          emoji: "👥",
          title: "Workshops and Training",
          description: "Reach more people at once - higher revenue, community building",
          items: [
            "Corporate training",
            "Public workshop",
            "Online webinar",
            "Conference presentation",
          ],
        },
        {
          emoji: "📚",
          title: "Digital Products",
          description: "Passive income - create once, sell continuously",
          items: [
            "Strategic guide PDF",
            "Online course package",
            "Templates and tools",
            "E-book and workbook",
          ],
        },
        {
          emoji: "🎫",
          title: "Retainer Packages",
          description: "Stable, predictable revenue - loyal clients guaranteed",
          items: [
            "Monthly consulting retainer",
            "Quarterly strategy package",
            "Annual VIP service",
            "Ongoing support",
          ],
        },
      ],
    },
    heroAnimation: {
      chat: {
        header: "Bespire AI Assistant",
        subheader: "Consulting Assistant · Online",
        user1: "Build me a professional consulting website!",
        ai1: "On it! I'll create an elegant site with your services, testimonials, and booking options.",
        user2: "Set my calendar so I take clients on Tuesdays and Thursdays.",
        ai2: "Done! Tuesday and Thursday set as consultation days. Clients can book online.",
        user3: "Send a reminder email to tomorrow's clients.",
        ai3: "Sent! 3 clients received reminders about tomorrow's consultations with time and location.",
        user4: "Great, thanks!",
        ai4: "You're welcome! Let me know if you need anything! 💼",
        inputPlaceholder: "Type a message...",
      },
    },
    websiteAnimation: {
      chatHeader: "Website Builder",
      chatSubheader: "AI assistant",
      chatPrompt: "Build a professional consulting website with services section and booking!",
      aiMsg1: "Great! Analyzing your consulting profile and creating the perfect website...",
      aiMsg2: "I've created the following sections:",
      blocks: {
        hero: "Introduction & expertise",
        services: "Services & pricing",
        pricing: "Booking system + testimonials",
      },
      doneLabel: "Your website is ready! Check it out:",
      domain: "growthconsulting.com",
      userFollowUp: "Add a blog section too!",
      aiFollowUpReply: "Done! Blog section added where you can publish professional articles. Want to modify anything else?",
    },
    calendarAnimation: {
      chatHeader: "Calendar Management",
      month: "March 2026",
      subtitle: "AI assistant",
      userAsk: "Show me tomorrow's schedule for consultations!",
      aiResponse: "Tomorrow's calendar - Tuesday, March 10:",
      slots: {
        slot1: "Peter N. - Strategy",
        slot2: "Tech Startup - Business plan",
        slot4: "Smith Ltd. - Follow-up",
      },
      freeLabel: "Available",
      userBook: "Book the 2:30 PM slot for Anna Toth for a coaching session!",
      aiConfirm: "Booked! Anna Toth - Coaching, 2:30 PM. Confirmation email sent.",
      notifSent: "Reminder sent to client",
      userThanks: "Great, thanks!",
      newBookingNotif: "New booking: Anna T. - Coaching 2:30 PM",
    },
    clientsAnimation: {
      chatHeader: "Client Management",
      chatSubheader: "AI assistant",
      userAsk: "Show me Peter Nagy's client profile!",
      clients: {
        client1: {
          name: "Peter Nagy",
          tag: "VIP Client",
          status: "Active coaching program",
        },
      },
      visitsLabel: "consultations",
      timeline: {
        firstMeeting: "First consultation",
        strategyBought: "Coaching package",
        lastSession: "Last session",
        renewal: "Package renewal",
      },
      userAction: "Send him a personal offer for the Q2 strategy package!",
      aiConfirm: "Personal offer sent! Q2 strategy package details and discounted price included.",
      stats: {
        retention: "Retention",
        vipClients: "VIP clients",
        growth: "Growth",
      },
    },
    teamAnimation: {
      chatHeader: "Team Management",
      chatSubheader: "AI assistant · Online",
      user1: "Show me the consulting team's status today!",
      ai1: "Here's your team overview for today:",
      staff: [
        { name: "Brian Kiss", role: "Senior Consultant", status: "Available" },
        { name: "Anna Szabo", role: "Business Coach", status: "In session" },
        { name: "Mark Toth", role: "Junior Consultant", status: "Day off" },
        { name: "Lili Varga", role: "Workshop Leader", status: "Available" },
      ],
      user2: "What's today's utilization and revenue?",
      ai2: "Today's summary:",
      statsBookings: "Consultations",
      statsUtilization: "Utilization",
      statsRevenue: "Revenue",
      user3: "Reassign Mark Toth's tomorrow clients to Brian Kiss!",
      ai3: "Reorganizing tomorrow's schedule...",
      reassignDone: "3 consultations reassigned to Brian Kiss. Clients notified.",
      user4: "Thank you!",
      ai4: "You're welcome! The team is running efficiently. Let me know if you need anything! 💼",
      inputPlaceholder: "Ask about your team...",
    },
  },
  photographers: {
    slug: "photographer",
    heroTitle: "AI assistant that helps your daily work",
    heroAnimation: {
      chat: {
        header: "Bespire AI Assistant",
        subheader: "Online · Always available",
        inputPlaceholder: "Type a message...",
        user1: "Build me a portfolio website with my work!",
        ai1: "Done! I've built your site with 3 sections: hero, gallery, and pricing. Here's your link: capture-daniel.bespire.app",
        user2: "Book a wedding shoot for the Smiths this Saturday!",
        ai2: "Booked! Smith Wedding - Saturday, all day. I've notified them too. 📸",
        user3: "Email everyone who had a newborn shoot last year!",
        ai3: "Campaign ready and sent to 42 clients. Already at 85% open rate!",
        user4: "Thanks, this is amazing! 😍",
        ai4: "You're welcome! Anything you need - website, calendar, clients, marketing - just focus on shooting! 📷",
      },
    },
    websiteAnimation: {
      chatHeader: "Website Builder",
      chatSubheader: "AI assistant",
      domain: "capture-daniel.bespire.app",
      chatPrompt: "Build my portfolio site with gallery and pricing!",
      aiMsg1: "Sure! I've started building your site. Just a few seconds...",
      aiMsg2: "Sections ready:",
      doneLabel: "Done! Your site is live at:",
      userFollowUp: "Can you change the colors to dark gray and gold?",
      aiFollowUpReply: "Done! Switched to dark gray and gold. Looks elegant! 🎨",
      blocks: {
        hero: "Hero section",
        services: "Photography types",
        pricing: "Pricing & packages",
      },
    },
    calendarAnimation: {
      chatHeader: "Calendar Management",
      month: "March 2026",
      subtitle: "AI assistant",
      synced: "Synced",
      freeLabel: "Available",
      userAsk: "What are my free slots next week?",
      aiResponse: "Your next week slots:",
      userBook: "Book the 11:00 slot for Eva Nagy - portrait session!",
      aiConfirm: "Done! 11:00 booked for Eva Nagy - Portrait session",
      notifSent: "Notification sent to client",
      userThanks: "Thanks, awesome! 📸",
      days: ["M", "T", "W", "T", "F", "S", "S"],
      slots: {
        slot1: "Smith - Wedding",
        slot2: "Corporate headshots (6 ppl)",
        slot3: "",
        slot4: "Family session",
        slot5: "",
      },
      newBookingNotif: "New booking: K. Szabo - Maternity 3:00 PM",
    },
    clientsAnimation: {
      chatHeader: "Client Management",
      chatSubheader: "AI assistant",
      userAsk: "Show me Anna Nagy's profile!",
      userAction: "Send her an anniversary shoot offer!",
      aiConfirm: "Done! Offer sent to Anna Nagy",
      visitsLabel: "sessions",
      clients: {
        client1: { name: "Anna Nagy", tag: "VIP", status: "Returning client" },
        client2: { name: "David Szabo", tag: "New", status: "Portrait session" },
        client3: { name: "Reka Kiss", tag: "VIP", status: "Annual package" },
        client4: { name: "Peter Toth", tag: "Corporate", status: "Retainer" },
      },
      tabs: {
        profile: "Profile",
        history: "History",
        notes: "Notes",
      },
      timeline: {
        firstVisit: "First session",
        passBought: "Annual package",
        session: "Maternity shoot",
        renewal: "Toddler shoot",
      },
      stats: {
        retention: "Retention",
        vipClients: "VIP clients",
        growth: "Growth",
      },
    },
    marketingAnimation: {
      chatHeader: "Marketing",
      title: "Marketing automation",
      subtitle: "AI assistant",
      userAsk: "Win back clients who had newborn photos last year!",
      aiCampaignIntro: "Campaign ready! Here's the email preview:",
      emailSubject: "Baby's growing up! 👶→🧒",
      emailPreview: "Toddler photo session discount",
      emailCta: "Book now!",
      userSend: "Send it now! 🚀",
      aiSentConfirm: "Sent to all 42 addresses!",
      userReply: "This is amazing! 😍",
      sentLabel: "Sent",
      opensLabel: "Opens",
      funnel: {
        audience: "Audience",
        audienceDetail: "42 newborn photo clients",
        content: "Content",
        contentDetail: "Toddler photo offer",
        sending: "Sending",
        sendingDetail: "Sent to 42 addresses",
        results: "Results",
        resultsDetail: "85% open rate",
        resultTitle: "Campaign result",
        live: "● Live",
        conversions: "Returning",
      },
    },
    teamAnimation: {
      chatHeader: "Team Management",
      chatSubheader: "AI assistant · Online",
      inputPlaceholder: "Type a message...",
      user1: "Show today's team! Who's working?",
      ai1: "Here's the team status:",
      user2: "How are we doing with weekly bookings?",
      ai2: "Here are this week's stats:",
      user3: "Mark Kiss called in sick, reassign his shoots!",
      ai3: "Reassigned Mark Kiss's 3 shoots to available photographers.",
      user4: "Great, thanks! 🙌",
      ai4: "You're welcome! I've also notified clients about the photographer change. Everything's in sync! 📸",
      reassignDone: "3 shoots reassigned · 3 clients notified",
      statsBookings: "Weekly bookings",
      statsUtilization: "Utilization",
      statsRevenue: "Monthly revenue",
      staff: [
        { name: "Daniel Kovacs", role: "Senior photographer", status: "Available" },
        { name: "Ben Nagy", role: "Wedding photographer", status: "On shoot" },
        { name: "Mark Kiss", role: "Portrait photographer", status: "Sick" },
        { name: "Anna Toth", role: "Event photographer", status: "Available" },
      ],
    },
    heroDescription: "Build your website, manage your calendar and marketing through chat - AI understands everything and helps you. For solo photographers and multi-photographer studios.",
    heroImageAlt: "Photographer at work",
    soloBadge: "For Solo Photographers",
    soloTitle: "Perfect for solo photographers",
    soloDescription: "If you work alone, Bespire AI is your perfect assistant. It handles appointments, clients, marketing - so you can focus on what you love.",
    teamBadge: "For Multi-Photographer Studios",
    teamTitle: "Complex studio management made simple",
    teamDescription: "If multiple photographers work in the studio, Bespire helps coordinate the team.",
    teamImageAlt: "Photography studio team collaboration",
    phoneDemo: {
      businessName: "Capture Studio",
      domain: "capturestudio.com",
      serviceName: "Portrait photography",
      duration: "2 hours",
      price: "$200",
      appointment: "Saturday, 2:00 PM",
      therapist: "Daniel",
      appointmentLabel: "Appointment",
      therapistLabel: "Photographer",
      confirmButton: "Confirm booking",
      url: "capturestudio.com/booking",
      tagline: "Professional photo & video services",
      selectService: "Select a service",
      services: [
        { name: "Portrait photography", duration: "2 hours", price: "$200" },
        { name: "Wedding photography", duration: "8 hours", price: "$2,500" },
        { name: "Corporate photography", duration: "4 hours", price: "$800" },
      ],
      selectTime: "Select a time",
      timeSlots: ["09:00", "11:00", "13:00", "15:00", "17:00", "19:00"],
      confirmed: "Booking confirmed!",
      confirmedSubtext: "We'll send you an email reminder",
      browserLabel: "From browser, no app needed",
      noAppNeeded: "No app download needed!",
    },
    aiChatExamples: {
      website: {
        userMessage: "Add a section with photography types and prices",
        aiResponse: "Done! I've created a section for services. Portrait, wedding, corporate photography - each with its own description and gallery. Would you like to modify it?",
        aiAction: "AI creates the photography section",
      },
      calendar: {
        userMessage: "What are my available slots next week for wedding photography?",
        aiResponse: "Next week I'm free all day Saturday and Sunday for wedding photography. Friday afternoon is also available. Should I book one?",
        aiAction: "AI instantly shows available slots",
      },
      marketing: {
        userMessage: "Create an email campaign for those who had newborn photos last year, they might want toddler photos now",
        aiResponse: "Found 42 clients who had newborn photos 1 year ago. Email ready with personalized message. Send now or schedule?",
        aiAction: "AI creates and targets the campaign",
      },
    },
    sellProducts: {
      title: "What can you sell as a photographer?",
      description: "Multiple revenue streams, one platform - with Bespire you can sell not just photo sessions, but digital products, courses, and consulting too",
      products: [
        {
          emoji: "📸",
          title: "Photo Sessions",
          description: "Your foundation - clients can book appointments online for all your sessions",
          items: [
            "Portrait and business photography",
            "Wedding photography",
            "Family and maternity photography",
            "Event and corporate photography",
          ],
        },
        {
          emoji: "🖼️",
          title: "Products and Prints",
          description: "Higher price category - premium products",
          items: [
            "Printed photo album",
            "Canvas and framed prints",
            "Acrylic and metal prints",
            "Digital image package",
          ],
        },
        {
          emoji: "👥",
          title: "Workshops",
          description: "Reach more people at once - higher revenue, community building",
          items: [
            "Photography basics workshop",
            "Lightroom editing course",
            "Portrait photography masterclass",
            "Mobile photography tips",
          ],
        },
        {
          emoji: "📚",
          title: "Digital Products",
          description: "Passive income - create once, sell continuously",
          items: [
            "Lightroom preset pack",
            "Photography guide for beginners",
            "Posing tips e-book",
            "Post-processing video course",
          ],
        },
        {
          emoji: "🎫",
          title: "Packages and Subscriptions",
          description: "Stable, predictable revenue - loyal clients guaranteed",
          items: [
            "Annual family photo package",
            "Corporate photo retainer",
            "Monthly content photography",
            "Gift vouchers",
          ],
        },
      ],
    },
  },
  nailTechnicians: {
    slug: "nail-technician",
    heroTitle: "AI assistant that helps your daily work",
    heroAnimation: {
      chat: {
        header: "Bespire AI Assistant",
        subheader: "Online · Always available",
        inputPlaceholder: "Type a message...",
        user1: "Build me a website with nail services and pricing!",
        ai1: "Done! I've built your site with 3 sections: hero, services and pricing. Here's your URL: nailsbeauty.bespire.com",
        user2: "Book Szabó Lila for gel polish tomorrow at 4 PM!",
        ai2: "Booked! Szabó Lila - Gel polish, tomorrow 4:00 PM. I've sent her a notification too.",
        user3: "Send an email to clients who haven't visited in 2 months!",
        ai3: "Campaign created and sent to 24 inactive clients. Already at 88% open rate!",
        user4: "Thanks, this is fantastic! 😍",
        ai4: "You're welcome! If you need anything, just ask. Website, calendar, clients, marketing - you just do nails! 💅",
      },
    },
    websiteAnimation: {
      chatHeader: "Website Builder",
      chatSubheader: "AI assistant",
      domain: "nailsbeauty.bespire.com",
      chatPrompt: "Build my page with nail services and pricing!",
      aiMsg1: "Sure! I've started building your site. Just a few seconds and it'll be ready...",
      aiMsg2: "Sections are done:",
      doneLabel: "Done! Your site is live and available:",
      userFollowUp: "Can you change the colors to pink and purple?",
      aiFollowUpReply: "Done! I've switched the colors to pink and purple. Looks gorgeous! 💅",
      blocks: {
        hero: "Hero section",
        services: "Nail services",
        pricing: "Pricing",
      },
    },
    calendarAnimation: {
      chatHeader: "Calendar Management",
      month: "March 2026",
      subtitle: "AI assistant",
      synced: "Synced",
      freeLabel: "Free",
      userAsk: "What are my free slots tomorrow?",
      aiResponse: "Your tomorrow's schedule:",
      userBook: "Book the 10:30 slot for Szabó Lila for gel polish!",
      aiConfirm: "Done! 10:30 booked for Szabó Lila - Gel polish",
      notifSent: "Notification sent to client",
      userThanks: "Thanks, great! 👍",
      days: ["M", "T", "W", "Th", "F", "Sa", "Su"],
      slots: {
        slot1: "Kiss A. - Gel polish",
        slot2: "Nagy E. - Nail extensions",
        slot3: "",
        slot4: "Tóth L. - Nail art",
        slot5: "",
      },
      newBookingNotif: "New booking: Kovács B. - Manicure 3:00 PM",
    },
    clientsAnimation: {
      chatHeader: "Client Management",
      chatSubheader: "AI assistant",
      userAsk: "Show me Szabó Lila's profile!",
      userAction: "Send her a reminder about her expiring pass!",
      aiConfirm: "Done! Reminder sent to Szabó Lila",
      visitsLabel: "visits",
      clients: {
        client1: { name: "Szabó Lila", tag: "VIP", status: "Active pass" },
        client2: { name: "Kiss Anna", tag: "New", status: "First manicure" },
        client3: { name: "Nagy Eszter", tag: "VIP", status: "8-session" },
        client4: { name: "Tóth Laura", tag: "Returning", status: "Monthly pass" },
      },
      tabs: {
        profile: "Profile",
        history: "History",
        notes: "Notes",
      },
      timeline: {
        firstVisit: "First visit",
        passBought: "8-session pass",
        session: "Gel polish #6",
        renewal: "Pass renewal",
      },
      stats: {
        retention: "Retention",
        vipClients: "VIP clients",
        growth: "Growth",
      },
    },
    marketingAnimation: {
      chatHeader: "Marketing",
      title: "Marketing automation",
      subtitle: "AI assistant",
      userAsk: "Bring back clients inactive for 2 months!",
      aiCampaignIntro: "I've created the campaign! Here's the email preview:",
      emailSubject: "We miss you!",
      emailPreview: "15% discount for returning clients",
      emailCta: "Book now!",
      userSend: "Send it now! 🚀",
      aiSentConfirm: "Sent to all 24 contacts!",
      userReply: "This is fantastic! 😍",
      sentLabel: "Sent",
      opensLabel: "Opens",
      funnel: {
        audience: "Audience",
        audienceDetail: "24 inactive clients",
        content: "Content",
        contentDetail: "Comeback offer email",
        sending: "Sending",
        sendingDetail: "Sent to 24 contacts",
        results: "Results",
        resultsDetail: "88% open rate",
        resultTitle: "Campaign results",
        live: "● Live",
        conversions: "Returning",
      },
    },
    teamAnimation: {
      chatHeader: "Team Management",
      chatSubheader: "AI assistant · Online",
      inputPlaceholder: "Type a message...",
      user1: "Show me today's team! Who's working?",
      ai1: "Here's today's team status:",
      user2: "How are today's bookings looking?",
      ai2: "Here are today's stats:",
      user3: "Horváth Réka is sick, redistribute her clients to the others!",
      ai3: "I've reassigned Horváth Réka's 5 clients to available nail techs.",
      user4: "Great, thanks! 🙌",
      ai4: "You're welcome! I've also notified clients about the nail tech change. Everything is in sync! 💅",
      reassignDone: "5 clients reassigned · 5 clients notified",
      statsBookings: "Today's bookings",
      statsUtilization: "Utilization",
      statsRevenue: "Weekly revenue",
      staff: [
        { name: "Kiss Petra", role: "Senior nail tech", status: "Available" },
        { name: "Nagy Zsófi", role: "Nail extensions", status: "With client" },
        { name: "Horváth Réka", role: "Gel polish spec.", status: "Sick" },
        { name: "Tóth Bea", role: "Nail artist", status: "Available" },
      ],
    },
    heroDescription: "Build your website, manage your calendar and marketing through chat - AI understands everything and helps you. For solo nail technicians and multi-tech salons.",
    heroImageAlt: "Nail technician doing manicure",
    soloBadge: "For Solo Nail Technicians",
    soloTitle: "Perfect for solo nail technicians",
    soloDescription: "If you work alone, Bespire AI is your perfect assistant. It handles appointments, clients, marketing - so you can focus on what you love.",
    teamBadge: "For Multi-Tech Nail Salons",
    teamTitle: "Complex salon management made simple",
    teamDescription: "If multiple nail techs work in the salon, Bespire helps coordinate the team.",
    teamImageAlt: "Nail salon team collaboration",
    phoneDemo: {
      businessName: "Nails & Beauty",
      domain: "nailsbeauty.com",
      serviceName: "Gel polish",
      duration: "60 min",
      price: "$40",
      appointment: "Friday, 4:00 PM",
      therapist: "Rebecca",
      appointmentLabel: "Appointment",
      therapistLabel: "Nail Tech",
      confirmButton: "Confirm booking",
      url: "nailsbeauty.com/booking",
      tagline: "Professional nail services",
      selectService: "Select a service",
      services: [
        { name: "Gel polish", duration: "60 min", price: "$40" },
        { name: "Acrylic nails", duration: "90 min", price: "$65" },
        { name: "Nail art", duration: "30 min", price: "$25" },
      ],
      selectTime: "Select a time",
      timeSlots: ["09:00", "10:30", "12:00", "14:00", "16:00", "18:00"],
      confirmed: "Booking confirmed!",
      confirmedSubtext: "We'll send you an SMS reminder",
      browserLabel: "From browser, no app needed",
      noAppNeeded: "No app download needed!",
    },
    aiChatExamples: {
      website: {
        userMessage: "Add a section with nail services and prices",
        aiResponse: "Done! I've created a section for services. Gel polish, nail extensions, nail art - each with its own description and gallery. Would you like to modify it?",
        aiAction: "AI creates the services section",
      },
      calendar: {
        userMessage: "What are my available slots tomorrow afternoon for nail extensions?",
        aiResponse: "Tomorrow afternoon you have slots at 1:00 PM, 3:30 PM, and 6:00 PM for a 90-minute nail extension. Should I book someone?",
        aiAction: "AI instantly shows available slots",
      },
      marketing: {
        userMessage: "Create an Instagram post with my latest spring nail designs",
        aiResponse: "Created a post from your gallery with spring designs. Hashtags added, description ready. Publish now or schedule?",
        aiAction: "AI creates the social media post",
      },
    },
    sellProducts: {
      title: "What can you sell as a nail technician?",
      description: "Multiple revenue streams, one platform - with Bespire you can sell not just nail services, but digital products, courses, and consulting too",
      products: [
        {
          emoji: "💅",
          title: "Nail Services",
          description: "Your foundation - clients can book appointments online for all your services",
          items: ["Gel polish, manicure", "Nail extensions", "Nail art and decorations", "Pedicure treatments"],
        },
        {
          emoji: "💎",
          title: "Premium Decorations",
          description: "Higher price category - extra services",
          items: ["Swarovski crystal decorations", "Hand-painted designs", "3D nail art", "Bridal nail design"],
        },
        {
          emoji: "👥",
          title: "Workshops",
          description: "Reach more people at once - higher revenue, community building",
          items: ["Home nail care course", "Gel polish basics workshop", "Bachelorette nail party", "Decoration masterclass"],
        },
        {
          emoji: "📚",
          title: "Digital Products",
          description: "Passive income - create once, sell continuously",
          items: ["Home nail care guide", "Design inspiration e-book", "Technique video course", "Product recommendations catalog"],
        },
        {
          emoji: "🎫",
          title: "Passes and Packages",
          description: "Stable, predictable revenue - loyal clients guaranteed",
          items: ["Monthly nail subscription", "VIP regular client package", "Annual beauty program", "Gift vouchers"],
        },
      ],
    },
  },
  psychologists: {
    slug: "psychologist",
    heroTitle: "AI assistant that helps your daily work",
    heroAnimation: {
      chat: {
        header: "Bespire AI Assistant",
        subheader: "Online · Always available",
        inputPlaceholder: "Type a message...",
        user1: "Build me a website with my services and methods!",
        ai1: "Done! I've built your site with 3 sections: introduction, services and booking. Here's your URL: harmony-psych.bespire.com",
        user2: "Book Nagy Éva for individual consultation on Wednesday at 4 PM!",
        ai2: "Booked! Nagy Éva - Individual consultation, Wednesday 4:00 PM. I've sent her a discreet notification too.",
        user3: "Send an email to those who inquired but didn't book!",
        ai3: "I've created a supportive, discreet email and sent it to 15 potential clients. Already at 76% open rate!",
        user4: "Thanks, this is fantastic! 😊",
        ai4: "You're welcome! If you need anything, just ask. Website, calendar, clients, marketing - you just focus on your clients! 🧠",
      },
    },
    websiteAnimation: {
      chatHeader: "Website Builder",
      chatSubheader: "AI assistant",
      domain: "harmony-psych.bespire.com",
      chatPrompt: "Build my page with services and methods!",
      aiMsg1: "Sure! I've started building your site. Just a few seconds and it'll be ready...",
      aiMsg2: "Sections are done:",
      doneLabel: "Done! Your site is live and available:",
      userFollowUp: "Can you change the colors to purple and white for a calmer feel?",
      aiFollowUpReply: "Done! I've switched the colors to purple and white. Calm, professional atmosphere! 🧘",
      blocks: {
        hero: "Introduction",
        services: "Services",
        pricing: "Booking and pricing",
      },
    },
    calendarAnimation: {
      chatHeader: "Calendar Management",
      month: "March 2026",
      subtitle: "AI assistant",
      synced: "Synced",
      freeLabel: "Free",
      userAsk: "What are my free slots tomorrow?",
      aiResponse: "Your tomorrow's schedule:",
      userBook: "Book the 2:00 PM slot for Nagy Éva!",
      aiConfirm: "Done! 2:00 PM booked for Nagy Éva",
      notifSent: "Discreet notification sent to client",
      userThanks: "Thanks, great! 🙏",
      days: ["M", "T", "W", "Th", "F", "Sa", "Su"],
      slots: {
        slot1: "Szabó K. - Individual consultation",
        slot2: "Kiss J. - Couples therapy",
        slot3: "Tóth M. - Stress management",
        slot4: "",
        slot5: "",
      },
      newBookingNotif: "New booking: Varga B. - Individual consultation 4:00 PM",
    },
    clientsAnimation: {
      chatHeader: "Client Management",
      chatSubheader: "AI assistant",
      userAsk: "Show me Nagy Éva's profile!",
      userAction: "Send her a reminder about her next consultation!",
      aiConfirm: "Done! Discreet reminder sent to Nagy Éva",
      visitsLabel: "sessions",
      clients: {
        client1: { name: "Nagy Éva", tag: "Regular", status: "Active therapy" },
        client2: { name: "Szabó Katalin", tag: "New", status: "First consultation" },
        client3: { name: "Kiss János", tag: "Regular", status: "Couples therapy" },
        client4: { name: "Tóth Márton", tag: "Returning", status: "Follow-up" },
      },
      tabs: {
        profile: "Profile",
        history: "History",
        notes: "Notes",
      },
      timeline: {
        firstVisit: "First consultation",
        program: "Anxiety management program",
        session: "Consultation #18",
        renewal: "Program continuation",
      },
      stats: {
        retention: "Retention",
        regularClients: "Regular clients",
        growth: "Growth",
      },
    },
    marketingAnimation: {
      chatHeader: "Marketing",
      title: "Marketing automation",
      subtitle: "AI assistant",
      userAsk: "Send an email to inquirers who didn't book!",
      aiCampaignIntro: "I've created the campaign! Here's the email preview:",
      emailSubject: "We can help - the first step is the most important",
      emailPreview: "Free introductory consultation",
      emailCta: "Book an appointment",
      userSend: "Send it now! 🚀",
      aiSentConfirm: "Sent to all 15 contacts!",
      userReply: "This is perfect! 🙏",
      sentLabel: "Sent",
      opensLabel: "Opens",
      funnel: {
        audience: "Audience",
        audienceDetail: "15 inquiring clients",
        content: "Content",
        contentDetail: "Encouraging email",
        sending: "Sending",
        sendingDetail: "Sent to 15 contacts",
        results: "Results",
        resultsDetail: "76% open rate",
        resultTitle: "Campaign results",
        live: "● Live",
        conversions: "New booking",
      },
    },
    teamAnimation: {
      chatHeader: "Practice Management",
      chatSubheader: "AI assistant · Online",
      inputPlaceholder: "Type a message...",
      user1: "Show me today's team! Who's working?",
      ai1: "Here's today's practice status:",
      user2: "How are today's consultations looking?",
      ai2: "Here are today's stats:",
      user3: "Dr. Kiss Éva is sick, redistribute her clients to the others!",
      ai3: "I've reassigned Dr. Kiss Éva's 4 consultations to available specialists.",
      user4: "Great, thanks! 🙌",
      ai4: "You're welcome! I've also discreetly notified clients about the specialist change. Everything is in sync! 🧠",
      reassignDone: "4 consultations reassigned · 4 clients notified",
      statsBookings: "Today's consultations",
      statsUtilization: "Utilization",
      statsRevenue: "Weekly revenue",
      staff: [
        { name: "Dr. Kovács Anna", role: "Clinical psychologist", status: "Available" },
        { name: "Dr. Nagy Bence", role: "Couples therapist", status: "In session" },
        { name: "Dr. Kiss Éva", role: "Child psychologist", status: "Sick" },
        { name: "Dr. Szabó Petra", role: "Coach/mentor", status: "Available" },
      ],
    },
    heroDescription: "Build your website, manage your calendar and marketing through chat - AI understands everything and helps you. For solo psychologists and multi-therapist practices.",
    heroImageAlt: "Psychologist in consultation",
    soloBadge: "For Solo Psychologists",
    soloTitle: "Perfect for solo psychologists",
    soloDescription: "If you work alone, Bespire AI is your perfect assistant. It handles appointments, clients, marketing - so you can focus on what you love.",
    teamBadge: "For Multi-Therapist Practices",
    teamTitle: "Complex practice management made simple",
    teamDescription: "If multiple specialists work in the practice, Bespire helps coordinate the work.",
    teamImageAlt: "Psychology practice team",
    phoneDemo: {
      businessName: "Harmony Psychology",
      domain: "harmonypsych.com",
      serviceName: "Individual consultation",
      duration: "50 min",
      price: "$90",
      appointment: "Wednesday, 4:00 PM",
      therapist: "Dr. Eva Kiss",
      appointmentLabel: "Appointment",
      therapistLabel: "Psychologist",
      confirmButton: "Confirm booking",
      url: "harmonypsych.com/booking",
      tagline: "Professional psychological support",
      selectService: "Select a service",
      services: [
        { name: "Individual consultation", duration: "50 min", price: "$90" },
        { name: "Couples therapy", duration: "90 min", price: "$140" },
        { name: "Stress management", duration: "60 min", price: "$100" },
      ],
      selectTime: "Select a time",
      timeSlots: ["09:00", "10:00", "11:00", "14:00", "16:00", "18:00"],
      confirmed: "Booking confirmed!",
      confirmedSubtext: "We'll send you an email reminder",
      browserLabel: "From browser, no app needed",
      noAppNeeded: "No app download needed!",
    },
    aiChatExamples: {
      website: {
        userMessage: "Add a section with my services and methods",
        aiResponse: "Done! I've created a section for services. Individual therapy, couples therapy, stress management - each with its own description. Would you like to modify it?",
        aiAction: "AI creates the services section",
      },
      calendar: {
        userMessage: "What are my available slots next week for new client intake?",
        aiResponse: "Next week you have slots on Tuesday at 2:00 PM, Wednesday at 10:00 AM, and Thursday at 4:00 PM for a 50-minute consultation. Should I book someone?",
        aiAction: "AI instantly shows available slots",
      },
      marketing: {
        userMessage: "Create an email for those who inquired but didn't book, remind them I can help",
        aiResponse: "Found 15 potential clients. Created a discreet, encouraging email. Should I send it?",
        aiAction: "AI creates and targets the campaign",
      },
    },
    sellProducts: {
      title: "What can you sell as a psychologist?",
      description: "Multiple revenue streams, one platform - with Bespire you can sell not just consultations, but digital products, courses, and workshops too",
      products: [
        {
          emoji: "💬",
          title: "Consultations",
          description: "Your foundation - clients can book appointments online for all your consultations",
          items: [
            "Individual psychological consultation",
            "Couples and family therapy",
            "Online consultation",
            "Crisis consultation",
          ],
        },
        {
          emoji: "🧠",
          title: "Therapeutic Programs",
          description: "Higher price category - structured programs",
          items: [
            "8-week anxiety management",
            "Self-esteem development program",
            "Stress management course",
            "Burnout prevention",
          ],
        },
        {
          emoji: "👥",
          title: "Group Sessions",
          description: "Reach more people at once - cost-effective solution for clients",
          items: [
            "Group therapy",
            "Self-awareness group",
            "Relaxation workshop",
            "Communication training",
          ],
        },
        {
          emoji: "📚",
          title: "Digital Products",
          description: "Passive income - create once, sell continuously",
          items: [
            "Self-awareness workbook",
            "Meditation audio files",
            "Stress management e-book",
            "Journal and reflection templates",
          ],
        },
        {
          emoji: "🎫",
          title: "Therapy Packages",
          description: "Stable, predictable revenue - committed clients",
          items: [
            "5-session consultation package",
            "Monthly support program",
            "VIP intensive package",
            "Follow-up packages",
          ],
        },
      ],
    },
  },
  nutritionConsultants: {
    slug: "nutrition-consultant",
    heroTitle: "AI assistant that helps your daily work",
    heroDescription: "Build your website, manage your calendar and marketing through chat - AI understands everything and helps you. For solo nutrition consultants and multi-consultant teams.",
    heroImageAlt: "Nutrition consultant in consultation",
    soloBadge: "For Solo Nutrition Consultants",
    soloTitle: "Perfect for solo nutrition consultants",
    soloDescription: "If you work alone, Bespire AI is your perfect assistant. It handles appointments, clients, marketing - so you can focus on what you love.",
    teamBadge: "For Multi-Consultant Teams",
    teamTitle: "Complex nutrition center management made simple",
    teamDescription: "If multiple specialists work in the team, Bespire helps coordinate the work.",
    teamImageAlt: "Nutrition consulting team",
    phoneDemo: {
      businessName: "NutriBalance",
      domain: "nutribalance.com",
      serviceName: "Diet consultation",
      duration: "60 min",
      price: "$75",
      appointment: "Monday, 10:00 AM",
      therapist: "Petra",
      appointmentLabel: "Appointment",
      therapistLabel: "Dietitian",
      confirmButton: "Confirm booking",
      url: "nutribalance.com/booking",
      tagline: "Personalized nutrition consulting",
      selectService: "Select a service",
      services: [
        { name: "Diet consultation", duration: "60 min", price: "$75" },
        { name: "Weight loss program", duration: "45 min", price: "$60" },
        { name: "Sports nutrition", duration: "60 min", price: "$85" },
      ],
      selectTime: "Select a time",
      timeSlots: ["08:00", "09:30", "11:00", "13:00", "15:00", "17:00"],
      confirmed: "Booking confirmed!",
      confirmedSubtext: "We'll send you an email reminder",
      browserLabel: "From browser, no app needed",
      noAppNeeded: "No app download needed!",
    },
    aiChatExamples: {
      website: {
        userMessage: "Add a section with my services and programs",
        aiResponse: "Done! I've created a section for services. Diet consultation, weight loss program, athlete nutrition - each with its own description. Would you like to modify it?",
        aiAction: "AI creates the services section",
      },
      calendar: {
        userMessage: "What are my available slots tomorrow for diet consultation?",
        aiResponse: "Tomorrow you have slots at 9:00 AM, 11:00 AM, and 2:00 PM for a 60-minute diet consultation. Should I book someone?",
        aiAction: "AI instantly shows available slots",
      },
      marketing: {
        userMessage: "Create an email campaign for New Year's resolution makers, offer them 20% discount on starter package",
        aiResponse: "Created a motivating campaign about New Year's goals. Target audience: previous inquiries. Send now?",
        aiAction: "AI creates and targets the campaign",
      },
    },
    sellProducts: {
      title: "What can you sell as a nutrition consultant?",
      description: "Multiple revenue streams, one platform - with Bespire you can sell not just consultations, but digital products, courses, and programs too",
      products: [
        {
          emoji: "🥗",
          title: "Consultations",
          description: "Your foundation - clients can book appointments online for all your consultations",
          items: [
            "Initial meeting and assessment",
            "Progress check-in",
            "Online consultation",
            "Follow-up visit",
          ],
        },
        {
          emoji: "📋",
          title: "Diet Programs",
          description: "Higher price category - complete programs",
          items: [
            "Personalized diet plan",
            "12-week weight loss program",
            "Athlete nutrition design",
            "Healthy lifestyle package",
          ],
        },
        {
          emoji: "👥",
          title: "Group Programs",
          description: "Reach more people at once - cost-effective solution",
          items: [
            "Group weight loss challenge",
            "Healthy cooking workshop",
            "Nutrition basics course",
            "Lifestyle change program",
          ],
        },
        {
          emoji: "📚",
          title: "Digital Products",
          description: "Passive income - create once, sell continuously",
          items: [
            "Recipe collection PDF",
            "Diet planner template",
            "Calorie counting guide",
            "Healthy snacks e-book",
          ],
        },
        {
          emoji: "🎫",
          title: "Subscriptions",
          description: "Stable, predictable revenue - committed clients",
          items: [
            "Monthly diet subscription",
            "Quarterly follow-up package",
            "VIP annual program",
            "Weekly recipe package",
          ],
        },
      ],
    },
    heroAnimation: {
      chat: {
        header: "Bespire AI Assistant",
        subheader: "Nutrition Assistant · Online",
        user1: "Build me a website with my services and diet plans!",
        ai1: "On it! I'll create an elegant site with diet consultations, programs, and online booking.",
        user2: "Set my calendar so I take clients on Mondays and Wednesdays.",
        ai2: "Done! Monday and Wednesday set as consultation days. Clients can book online.",
        user3: "Send a reminder email to tomorrow's clients about their diet consultation.",
        ai3: "Sent! 5 clients received reminders about tomorrow's consultations with times and preparation notes.",
        user4: "Great, thanks! 🙏",
        ai4: "You're welcome! Let me know if you need anything! 🥗",
        inputPlaceholder: "Type a message...",
      },
    },
    websiteAnimation: {
      chatHeader: "Website Builder",
      chatSubheader: "AI assistant",
      chatPrompt: "Build a professional nutrition consulting website with services and booking!",
      aiMsg1: "Great! Analyzing your profile and creating the perfect website...",
      aiMsg2: "I've created the following sections:",
      blocks: {
        hero: "Introduction & specialties",
        services: "Diet programs & pricing",
        pricing: "Booking system + testimonials",
      },
      doneLabel: "Your website is ready! Check it out:",
      domain: "nutribalance.bespire.com",
      userFollowUp: "Add a recipe blog section too!",
      aiFollowUpReply: "Done! Recipe blog section added where you can publish healthy recipes and nutrition tips. Want to modify anything else?",
    },
    calendarAnimation: {
      chatHeader: "Calendar Management",
      month: "March 2026",
      subtitle: "AI assistant",
      userAsk: "Show me tomorrow's schedule for consultations!",
      aiResponse: "Tomorrow's calendar - Monday, March 9:",
      slots: {
        slot1: "Anna K. - Diet assessment",
        slot2: "Kate S. - Weight loss check-in",
        slot4: "Mary T. - Athlete nutrition",
      },
      freeLabel: "Available",
      userBook: "Book the 11:00 AM slot for Eva Kiss for a diet consultation!",
      aiConfirm: "Booked! Eva Kiss - Diet consultation, 11:00 AM. Confirmation email sent.",
      notifSent: "Reminder sent to client",
      userThanks: "Great, thanks! 👍",
      newBookingNotif: "New booking: Eva K. - Diet consultation 11:00 AM",
    },
    clientsAnimation: {
      chatHeader: "Client Management",
      chatSubheader: "AI assistant",
      userAsk: "Show me Anna Kovacs's client profile!",
      clients: {
        client1: {
          name: "Anna Kovacs",
          tag: "VIP Client",
          status: "Active weight loss program",
        },
      },
      visitsLabel: "consultations",
      timeline: {
        firstMeeting: "First consultation",
        strategyBought: "12-week program",
        lastSession: "Last check-in",
        renewal: "Program renewal",
      },
      userAction: "Send her a personal offer for the spring detox program!",
      aiConfirm: "Personal offer sent! Spring detox program details and discounted price included.",
      stats: {
        retention: "Retention",
        vipClients: "VIP clients",
        growth: "Growth",
      },
    },
    teamAnimation: {
      chatHeader: "Team Management",
      chatSubheader: "AI assistant · Online",
      user1: "Show me the dietitian team's status today!",
      ai1: "Here's your team overview for today:",
      staff: [
        { name: "Petra Nagy", role: "Lead Dietitian", status: "Available" },
        { name: "Reka Kiss", role: "Sports Dietitian", status: "In session" },
        { name: "Luca Szabo", role: "Clinical Dietitian", status: "Day off" },
        { name: "Ben Toth", role: "Nutrition Consultant", status: "Available" },
      ],
      user2: "What's today's utilization and revenue?",
      ai2: "Today's summary:",
      statsBookings: "Consultations",
      statsUtilization: "Utilization",
      statsRevenue: "Revenue",
      user3: "Reassign Luca Szabo's tomorrow clients to Petra Nagy!",
      ai3: "Reorganizing tomorrow's schedule...",
      reassignDone: "4 consultations reassigned to Petra Nagy. Clients notified.",
      user4: "Thank you!",
      ai4: "You're welcome! The team is running efficiently. Let me know if you need anything! 🥗",
      inputPlaceholder: "Ask about your team...",
    },
  },
  physiotherapists: {
    slug: "physiotherapist",
    heroTitle: "AI assistant that helps your daily work",
    heroAnimation: {
      chat: {
        header: "Bespire AI Assistant",
        subheader: "Online · Always available",
        inputPlaceholder: "Type a message...",
        user1: "Build me a website with treatments and pricing!",
        ai1: "Done! I've built your site with 3 sections: hero, treatments and pricing. Here's your URL: movementtherapy.bespire.com",
        user2: "Book Tóth Mária for spine treatment tomorrow at 9 AM!",
        ai2: "Booked! Tóth Mária - Spine treatment, tomorrow 9:00 AM. I've sent her a notification too.",
        user3: "Send an email to those who completed treatment, remind them about follow-up!",
        ai3: "Campaign created and sent to 33 patients. Already at 85% open rate!",
        user4: "Thanks, this is fantastic! 😍",
        ai4: "You're welcome! If you need anything, just ask. Website, calendar, patients, marketing - you just heal! 🩺",
      },
    },
    websiteAnimation: {
      chatHeader: "Website Builder",
      chatSubheader: "AI assistant",
      domain: "movementtherapy.bespire.com",
      chatPrompt: "Build my page with treatments and pricing!",
      aiMsg1: "Sure! I've started building your site. Just a few seconds and it'll be ready...",
      aiMsg2: "Sections are done:",
      doneLabel: "Done! Your site is live and available:",
      userFollowUp: "Can you change the colors to teal and white?",
      aiFollowUpReply: "Done! I've switched the colors to teal and white. Professional look! 🏥",
      blocks: {
        hero: "Hero section",
        services: "Treatments",
        pricing: "Pricing",
      },
    },
    calendarAnimation: {
      chatHeader: "Calendar Management",
      month: "March 2026",
      subtitle: "AI assistant",
      synced: "Synced",
      freeLabel: "Free",
      userAsk: "What are my free slots tomorrow?",
      aiResponse: "Your tomorrow's schedule:",
      userBook: "Book the 9:00 slot for Tóth Mária!",
      aiConfirm: "Done! 09:00 booked for Tóth Mária",
      notifSent: "Notification sent to patient",
      userThanks: "Thanks, great! 👍",
      days: ["M", "T", "W", "Th", "F", "Sa", "Su"],
      slots: {
        slot1: "Nagy P. - Spine treatment",
        slot2: "Kovács A. - Sports rehab",
        slot3: "",
        slot4: "Kiss É. - Follow-up check",
        slot5: "",
      },
      newBookingNotif: "New booking: Szabó J. - Manual therapy 2:00 PM",
    },
    clientsAnimation: {
      chatHeader: "Patient Management",
      chatSubheader: "AI assistant",
      userAsk: "Show me Szabó János's profile!",
      userAction: "Send him a reminder about his follow-up check!",
      aiConfirm: "Done! Reminder sent to Szabó János",
      visitsLabel: "visits",
      clients: {
        client1: { name: "Szabó János", tag: "Returning", status: "Active treatment" },
        client2: { name: "Tóth Mária", tag: "New", status: "First examination" },
        client3: { name: "Kiss Éva", tag: "VIP", status: "Rehabilitation" },
        client4: { name: "Nagy Péter", tag: "Returning", status: "Follow-up" },
      },
      tabs: {
        profile: "Profile",
        history: "History",
        notes: "Notes",
      },
      timeline: {
        firstVisit: "First examination",
        passBought: "10-session package",
        session: "Spine treatment #8",
        renewal: "Package renewal",
      },
      stats: {
        retention: "Retention",
        vipClients: "VIP patients",
        growth: "Growth",
      },
    },
    marketingAnimation: {
      chatHeader: "Marketing",
      title: "Marketing automation",
      subtitle: "AI assistant",
      userAsk: "Remind patients who completed their treatment!",
      aiCampaignIntro: "I've created the campaign! Here's the email preview:",
      emailSubject: "Time for your follow-up check!",
      emailPreview: "Follow-up check reminder",
      emailCta: "Book now!",
      userSend: "Send it now! 🚀",
      aiSentConfirm: "Sent to all 33 contacts!",
      userReply: "This is fantastic! 😍",
      sentLabel: "Sent",
      opensLabel: "Opens",
      funnel: {
        audience: "Audience",
        audienceDetail: "33 completed treatments",
        content: "Content",
        contentDetail: "Follow-up reminder email",
        sending: "Sending",
        sendingDetail: "Sent to 33 contacts",
        results: "Results",
        resultsDetail: "85% open rate",
        resultTitle: "Campaign results",
        live: "● Live",
        conversions: "Returning",
      },
    },
    teamAnimation: {
      chatHeader: "Team Management",
      chatSubheader: "AI assistant · Online",
      inputPlaceholder: "Type a message...",
      user1: "Show me today's team! Who's working?",
      ai1: "Here's today's team status:",
      user2: "How are today's bookings looking?",
      ai2: "Here are today's stats:",
      user3: "Dr. Kiss is sick, redistribute her treatments to the others!",
      ai3: "I've reassigned Dr. Kiss's 6 treatments to available specialists.",
      user4: "Great, thanks! 🙌",
      ai4: "You're welcome! I've also notified patients about the change. Everything is in sync! 🩺",
      reassignDone: "6 treatments reassigned · 4 patients notified",
      statsBookings: "Today's bookings",
      statsUtilization: "Utilization",
      statsRevenue: "Weekly revenue",
      staff: [
        { name: "Dr. Kovács Anna", role: "Physiotherapist", status: "Available" },
        { name: "Dr. Nagy Bence", role: "Manual therapist", status: "In session" },
        { name: "Dr. Kiss Réka", role: "Sports rehab", status: "Sick" },
        { name: "Dr. Tóth Márk", role: "Spine specialist", status: "Available" },
      ],
    },
    heroDescription: "Build your website, manage your calendar and marketing through chat - AI understands everything and helps you. For solo physiotherapists and multi-specialist clinics.",
    heroImageAlt: "Physiotherapist performing treatment",
    soloBadge: "For Solo Physiotherapists",
    soloTitle: "Perfect for solo physiotherapists",
    soloDescription: "If you work alone, Bespire AI is your perfect assistant. It handles appointments, patients, marketing - so you can focus on what you love.",
    teamBadge: "For Multi-Specialist Clinics",
    teamTitle: "Complex rehabilitation clinic management made simple",
    teamDescription: "If multiple specialists work at the clinic, Bespire helps coordinate the work.",
    teamImageAlt: "Physiotherapy clinic team",
    phoneDemo: {
      businessName: "MovementTherapy",
      domain: "movementtherapy.com",
      serviceName: "Spine treatment",
      duration: "45 min",
      price: "$60",
      appointment: "Thursday, 9:00 AM",
      therapist: "Thomas",
      appointmentLabel: "Appointment",
      therapistLabel: "Physiotherapist",
      confirmButton: "Confirm booking",
      url: "movementtherapy.com/booking",
      tagline: "Professional physiotherapy & rehabilitation",
      selectService: "Select a treatment",
      services: [
        { name: "Spine treatment", duration: "45 min", price: "$60" },
        { name: "Sports rehabilitation", duration: "60 min", price: "$75" },
        { name: "Movement development", duration: "30 min", price: "$45" },
      ],
      selectTime: "Select a time",
      timeSlots: ["08:00", "09:00", "10:00", "11:00", "14:00", "16:00"],
      confirmed: "Booking confirmed!",
      confirmedSubtext: "We'll send you an SMS reminder",
      browserLabel: "From browser, no app needed",
      noAppNeeded: "No app download needed!",
    },
    aiChatExamples: {
      website: {
        userMessage: "Add a section with treatment types and prices",
        aiResponse: "Done! I've created a section for treatments. Spine therapy, sports rehabilitation, movement development - each with its own description. Would you like to modify it?",
        aiAction: "AI creates the treatments section",
      },
      calendar: {
        userMessage: "What are my available morning slots tomorrow for spine treatment?",
        aiResponse: "Tomorrow morning you have slots at 8:00 AM, 9:00 AM, and 10:30 AM for a 45-minute spine treatment. Should I book someone?",
        aiAction: "AI instantly shows available slots",
      },
      marketing: {
        userMessage: "Create an email campaign for those who completed treatment, remind them about the follow-up check",
        aiResponse: "Found 33 patients who completed treatment 3 months ago. Email ready with follow-up check reminder. Send?",
        aiAction: "AI creates and targets the campaign",
      },
    },
    sellProducts: {
      title: "What can you sell as a physiotherapist?",
      description: "Multiple revenue streams, one platform - with Bespire you can sell not just treatments, but digital products, courses, and programs too",
      products: [
        {
          emoji: "🏥",
          title: "Treatments",
          description: "Your foundation - patients can book appointments online for all your treatments",
          items: [
            "Spine therapy and manual therapy",
            "Sports rehabilitation",
            "Movement development",
            "Follow-up examination",
          ],
        },
        {
          emoji: "📋",
          title: "Rehabilitation Programs",
          description: "Higher price category - complete recovery programs",
          items: [
            "Spine rehabilitation program",
            "Post-surgery recovery",
            "Athlete return program",
            "Chronic pain treatment",
          ],
        },
        {
          emoji: "👥",
          title: "Group Sessions",
          description: "Reach more people at once - prevention programs",
          items: [
            "Spine exercise group",
            "Posture correction class",
            "Office workers' exercise",
            "Senior movement program",
          ],
        },
        {
          emoji: "📚",
          title: "Digital Products",
          description: "Passive income - create once, sell continuously",
          items: [
            "Home exercise videos",
            "Spine protection guide PDF",
            "Stretching routine pack",
            "Ergonomic tips e-book",
          ],
        },
        {
          emoji: "🎫",
          title: "Treatment Packages",
          description: "Stable, predictable revenue - committed patients",
          items: [
            "10-session treatment package",
            "Rehabilitation package",
            "Monthly maintenance program",
            "VIP priority care",
          ],
        },
      ],
    },
  },
  sportsClubs: {
    slug: "sports-club",
    heroAnimation: {
      chat: {
        header: "Bespire AI Assistant",
        subheader: "Online · Always available",
        inputPlaceholder: "Type a message...",
        user1: "Build me a website with classes and schedule!",
        ai1: "Done! I've built your site with 3 sections: hero, classes and schedule. Here's your URL: powerfit.bespire.com",
        user2: "How many spots are available in tonight's CrossFit class at 6 PM?",
        ai2: "Tonight at 6:00 PM the CrossFit class has 6 available spots out of 15. Should I book someone?",
        user3: "Send a New Year campaign, offer 2 weeks free trial!",
        ai3: "Campaign created and sent to 45 prospects. Already at 78% open rate!",
        user4: "Great, thanks! 💪",
        ai4: "You're welcome! If you need anything, just ask. Website, calendar, members, marketing - you just train! 🏋️",
      },
    },
    websiteAnimation: {
      chatHeader: "Website Builder",
      chatSubheader: "AI assistant",
      domain: "powerfit.bespire.com",
      chatPrompt: "Build my page with class schedule and memberships!",
      aiMsg1: "Sure! I've started building your site. Just a few seconds and it'll be ready...",
      aiMsg2: "Sections are done:",
      doneLabel: "Done! Your site is live and available:",
      userFollowUp: "Can you change the colors to orange and dark gray?",
      aiFollowUpReply: "Done! I've switched the colors to orange and dark gray. Check it out! 🎨",
      blocks: {
        hero: "Hero section",
        services: "Classes and schedule",
        pricing: "Memberships and passes",
      },
    },
    calendarAnimation: {
      chatHeader: "Calendar Management",
      month: "March 2026",
      subtitle: "AI assistant",
      synced: "Synced",
      freeLabel: "Free",
      userAsk: "What are the free slots at the gym tomorrow?",
      aiResponse: "Tomorrow's gym schedule:",
      userBook: "Book the 8:00 slot for a CrossFit class!",
      aiConfirm: "Done! 08:00 booked for CrossFit class (max 15 people)",
      notifSent: "Notification sent to members",
      userThanks: "Thanks, great! 👍",
      days: ["M", "T", "W", "Th", "F", "Sa", "Su"],
      slots: {
        slot1: "CrossFit beginner (12 ppl)",
        slot2: "Spinning group (10 ppl)",
        slot3: "TRX advanced (8 ppl)",
        slot4: "HIIT workout (15 ppl)",
        slot5: "Yoga (6 ppl)",
      },
      newBookingNotif: "New booking: Kovács B. - CrossFit 6:00 PM",
    },
    clientsAnimation: {
      chatHeader: "Member Management",
      chatSubheader: "AI assistant",
      userAsk: "Show me Varga Gábor's profile!",
      userAction: "Send him a reminder about his expiring pass!",
      aiConfirm: "Done! Reminder sent to Varga Gábor",
      visitsLabel: "visits",
      clients: {
        client1: { name: "Varga Gábor", tag: "VIP", status: "Unlimited membership" },
        client2: { name: "Szabó Petra", tag: "New", status: "Trial session" },
        client3: { name: "Nagy Tamás", tag: "VIP", status: "10-session" },
        client4: { name: "Kiss Eszter", tag: "Returning", status: "Monthly pass" },
      },
      tabs: {
        profile: "Profile",
        history: "History",
        notes: "Notes",
      },
      timeline: {
        firstVisit: "First visit",
        passBought: "10-session pass",
        session: "CrossFit class #42",
        renewal: "Membership renewal",
      },
      stats: {
        retention: "Retention",
        vipClients: "VIP members",
        growth: "Growth",
      },
    },
    teamAnimation: {
      chatHeader: "Team Management",
      chatSubheader: "AI assistant · Online",
      inputPlaceholder: "Type a message...",
      user1: "Show me today's team! Who's working?",
      ai1: "Here's today's team status:",
      user2: "How are today's bookings looking?",
      ai2: "Here are today's stats:",
      user3: "Molnár Péter is sick, redistribute his classes to the others!",
      ai3: "I've reassigned Molnár Péter's 6 classes to available trainers.",
      user4: "Great, thanks! 🙌",
      ai4: "You're welcome! I've also notified members about the trainer change. Everything is in sync! 💪",
      reassignDone: "6 classes reassigned · 8 members notified",
      statsBookings: "Today's bookings",
      statsUtilization: "Utilization",
      statsRevenue: "Weekly revenue",
      staff: [
        { name: "Kovács Dániel", role: "CrossFit trainer", status: "Available" },
        { name: "Nagy Zsolt", role: "Personal trainer", status: "In session" },
        { name: "Molnár Péter", role: "Group trainer", status: "Sick" },
        { name: "Tóth Réka", role: "Spinning trainer", status: "Available" },
      ],
    },
    heroTitle: "AI assistant that helps your daily work",
    heroDescription: "Build your website, manage your calendar and marketing through chat - AI understands everything and helps you. For smaller sports clubs and larger fitness centers.",
    heroImageAlt: "Sports club training",
    soloBadge: "For Smaller Clubs",
    soloTitle: "Perfect for smaller sports clubs",
    soloDescription: "If you run a smaller club, Bespire AI is your perfect assistant. It handles classes, members, marketing - so you can focus on what you love.",
    teamBadge: "For Larger Fitness Centers",
    teamTitle: "Complex fitness center management made simple",
    teamDescription: "If multiple trainers and many members are involved, Bespire helps coordinate operations.",
    teamImageAlt: "Sports club team",
    phoneDemo: {
      businessName: "PowerFit Gym",
      domain: "powerfit.com",
      serviceName: "CrossFit class",
      duration: "60 min",
      price: "$18",
      appointment: "Tuesday, 6:00 PM",
      therapist: "Greg",
      appointmentLabel: "Appointment",
      therapistLabel: "Trainer",
      confirmButton: "Confirm registration",
      url: "powerfit.com/booking",
      tagline: "Group classes & personal training",
      selectService: "Select a class",
      services: [
        { name: "CrossFit class", duration: "60 min", price: "$18" },
        { name: "Spinning", duration: "45 min", price: "$15" },
        { name: "TRX training", duration: "50 min", price: "$18" },
      ],
      selectTime: "Select a time",
      timeSlots: ["06:00", "07:30", "09:00", "17:00", "18:30", "20:00"],
      confirmed: "Registration confirmed!",
      confirmedSubtext: "We'll send you an SMS reminder",
      browserLabel: "From browser, no app needed",
      noAppNeeded: "No app download needed!",
    },
    aiChatExamples: {
      website: {
        userMessage: "Add a section with classes and schedule",
        aiResponse: "Done! I've created a section for classes. CrossFit, spinning, TRX - each with its own description and schedule. Would you like to modify it?",
        aiAction: "AI creates the classes section",
      },
      calendar: {
        userMessage: "How many spots are available in tonight's CrossFit class?",
        aiResponse: "Tonight at 6:00 PM the CrossFit class has 6 available spots out of 15. Should I book someone?",
        aiAction: "AI instantly shows available spots",
      },
      marketing: {
        userMessage: "Create a campaign for the new year, offer 2 weeks free trial to new members",
        aiResponse: "Created a motivating campaign for New Year's resolutions. 2 weeks free trial offer. Send to prospects?",
        aiAction: "AI creates and targets the campaign",
      },
    },
    sellProducts: {
      title: "What can you sell as a sports club?",
      description: "Multiple revenue streams, one platform - with Bespire you can sell not just classes, but memberships, merchandise, and programs too",
      products: [
        {
          emoji: "🏋️",
          title: "Classes and Workouts",
          description: "Your foundation - members can register online for all your classes",
          items: [
            "CrossFit and functional training",
            "Spinning and cardio classes",
            "TRX and strength training",
            "Yoga and stretching",
          ],
        },
        {
          emoji: "💪",
          title: "Personal Training",
          description: "Higher price category - individual attention",
          items: [
            "Personal training",
            "Duo training",
            "Small group training",
            "Online coaching",
          ],
        },
        {
          emoji: "👥",
          title: "Special Programs",
          description: "Goal-oriented programs - higher commitment",
          items: [
            "8-week transformation challenge",
            "Summer body shaping program",
            "Competition preparation",
            "Beginner bootcamp",
          ],
        },
        {
          emoji: "🛒",
          title: "Products",
          description: "Supplementary revenue - webshop integration",
          items: [
            "Protein and supplements",
            "Workout clothing and equipment",
            "Club merchandise",
            "Healthy snacks",
          ],
        },
        {
          emoji: "🎫",
          title: "Memberships and Passes",
          description: "Stable, predictable revenue - loyal members",
          items: [
            "Monthly unlimited membership",
            "10-session pass",
            "VIP annual membership",
            "Corporate package",
          ],
        },
      ],
    },
  },
  teachers: {
    slug: "therapist-coach",
    heroTitle: "AI assistant that helps your daily work",
    heroDescription: "Build your website, manage your calendar and marketing through chat - AI understands everything and helps you. For solo therapists and coaches, as well as multi-specialist teams.",
    heroImageAlt: "Therapist in consultation",
    soloBadge: "For Solo Therapists & Coaches",
    soloTitle: "Perfect for solo therapists and coaches",
    soloDescription: "If you work alone, Bespire AI is your perfect assistant. It handles sessions, clients, marketing - so you can focus on what you love.",
    teamBadge: "For Multi-Specialist Teams",
    teamTitle: "Complex therapy and coaching center management made simple",
    teamDescription: "If multiple therapists or coaches work in the team, Bespire helps coordinate the work.",
    teamImageAlt: "Therapy and coaching team",
    phoneDemo: {
      businessName: "Inner Balance",
      domain: "innerbalance.com",
      serviceName: "Individual coaching session",
      duration: "60 min",
      price: "$75",
      appointment: "Monday, 10:00 AM",
      therapist: "Andrea",
      appointmentLabel: "Appointment",
      therapistLabel: "Therapist",
      confirmButton: "Confirm booking",
      url: "innerbalance.com/booking",
      tagline: "Therapy & coaching services",
      selectService: "Select a service",
      services: [
        { name: "Individual coaching", duration: "60 min", price: "$75" },
        { name: "Group therapy", duration: "90 min", price: "$45" },
        { name: "Online consultation", duration: "50 min", price: "$60" },
      ],
      selectTime: "Select a time",
      timeSlots: ["09:00", "10:00", "11:00", "14:00", "16:00", "18:00"],
      confirmed: "Booking confirmed!",
      confirmedSubtext: "We'll send you an email reminder",
      browserLabel: "From browser, no app needed",
      noAppNeeded: "No app download needed!",
    },
    aiChatExamples: {
      website: {
        userMessage: "Add a section with my services and coaching programs",
        aiResponse: "Done! I've created a section. Individual coaching, group therapy, online consultation - each with its own description. Would you like to modify it?",
        aiAction: "AI creates the services section",
      },
      calendar: {
        userMessage: "What are my available slots next week for individual coaching?",
        aiResponse: "Next week you have slots on Monday at 10:00 AM, Wednesday at 2:00 PM, and Friday at 11:00 AM for a 60-minute coaching session. Should I book someone?",
        aiAction: "AI instantly shows available slots",
      },
      marketing: {
        userMessage: "Create an email campaign for my new coaching program, offer 15% discount on the first session",
        aiResponse: "Created a motivating campaign for the new coaching program. 15% discount on the first session. Target: previous inquiries. Send?",
        aiAction: "AI creates and targets the campaign",
      },
    },
    sellProducts: {
      title: "What can you sell as a therapist or coach?",
      description: "Multiple revenue streams, one platform - with Bespire you can sell not just sessions, but digital products, programs, and workshops too",
      products: [
        {
          emoji: "🧘",
          title: "Sessions and Consultations",
          description: "Your foundation - clients can book online for all your services",
          items: [
            "Individual coaching session",
            "Couples therapy",
            "Online consultation",
            "Follow-up session",
          ],
        },
        {
          emoji: "🎯",
          title: "Coaching Programs",
          description: "Higher price category - long-term collaboration",
          items: [
            "3-month coaching package",
            "Lifestyle change program",
            "VIP intensive weekend",
            "Group coaching program",
          ],
        },
        {
          emoji: "👥",
          title: "Workshops and Groups",
          description: "Reach more people at once - community building",
          items: [
            "Self-discovery workshop",
            "Stress management training",
            "Mindfulness group",
            "Personal growth weekend",
          ],
        },
        {
          emoji: "📚",
          title: "Digital Products",
          description: "Passive income - create once, sell continuously",
          items: [
            "Self-discovery workbook PDF",
            "Meditation audio",
            "Online course package",
            "Coaching templates and tools",
          ],
        },
        {
          emoji: "🎫",
          title: "Passes and Subscriptions",
          description: "Stable, predictable revenue - committed clients",
          items: [
            "Monthly coaching package",
            "Quarterly follow-up",
            "VIP annual program",
            "Online content subscription",
          ],
        },
      ],
    },
    heroAnimation: {
      chat: {
        header: "Bespire AI Assistant",
        subheader: "Therapy Assistant · Online",
        user1: "Build me a website with my services and coaching programs!",
        ai1: "On it! I'll create a professional site with services, programs, and online booking.",
        user2: "Set my calendar so I take clients Monday to Friday mornings.",
        ai2: "Done! Mon-Fri mornings set. Clients can book individual sessions or group programs online.",
        user3: "Send a reminder email to tomorrow's clients about their session!",
        ai3: "Sent! 6 clients received reminders about tomorrow's session and preparation notes.",
        user4: "Great, thanks! 🙏",
        ai4: "You're welcome! Let me know if you need anything! 🧘",
        inputPlaceholder: "Type a message...",
      },
    },
    websiteAnimation: {
      chatHeader: "Website Builder",
      chatSubheader: "AI assistant",
      chatPrompt: "Build a professional therapist website with services and online booking!",
      aiMsg1: "Great! Analyzing your profile and creating the perfect website...",
      aiMsg2: "I've created the following sections:",
      blocks: {
        hero: "Introduction & specialties",
        services: "Services & pricing",
        pricing: "Booking + testimonials",
      },
      doneLabel: "Your website is ready! Check it out:",
      domain: "innerbalance.bespire.com",
      userFollowUp: "Add a client testimonials section too!",
      aiFollowUpReply: "Done! Testimonials section added where your clients can share their experiences. Want to modify anything else?",
    },
    calendarAnimation: {
      chatHeader: "Calendar Management",
      month: "March 2026",
      subtitle: "AI assistant",
      userAsk: "Show me tomorrow's schedule!",
      aiResponse: "Tomorrow's calendar - Monday, March 9:",
      slots: {
        slot1: "Anna K. - Individual coaching",
        slot2: "Small group - Stress management",
        slot4: "Peter T. - Follow-up session",
      },
      freeLabel: "Available",
      userBook: "Book the 11:00 AM slot for Emma Szabo for individual coaching!",
      aiConfirm: "Booked! Emma Szabo - Individual coaching, 11:00 AM. Confirmation email sent.",
      notifSent: "Reminder sent to client",
      userThanks: "Great, thanks! 👍",
      newBookingNotif: "New booking: Emma S. - Individual coaching 11:00 AM",
    },
    clientsAnimation: {
      chatHeader: "Client Management",
      chatSubheader: "AI assistant",
      userAsk: "Show me Anna Kovacs's client profile!",
      clients: {
        client1: {
          name: "Anna Kovacs",
          tag: "VIP Client",
          status: "Active coaching program",
        },
      },
      visitsLabel: "sessions",
      timeline: {
        firstMeeting: "First consultation",
        strategyBought: "Coaching package",
        lastSession: "Last session",
        renewal: "Program renewal",
      },
      userAction: "Send her a personal offer for the spring renewal program!",
      aiConfirm: "Personal offer sent! Spring renewal program details and discounted price included.",
      stats: {
        retention: "Retention",
        vipClients: "VIP clients",
        growth: "Growth",
      },
    },
    teamAnimation: {
      chatHeader: "Team Management",
      chatSubheader: "AI assistant · Online",
      user1: "Show me the therapy team's status today!",
      ai1: "Here's your team overview for today:",
      staff: [
        { name: "Andrea Kiss", role: "Life Coach", status: "Available" },
        { name: "Martin Szabo", role: "Family Therapist", status: "In session" },
        { name: "Kate Toth", role: "Stress Coach", status: "Day off" },
        { name: "Sophie Varga", role: "Mindfulness Coach", status: "Available" },
      ],
      user2: "What's today's utilization and session count?",
      ai2: "Today's summary:",
      statsBookings: "Sessions",
      statsUtilization: "Utilization",
      statsRevenue: "Revenue",
      user3: "Reassign Kate Toth's tomorrow clients to Andrea Kiss!",
      ai3: "Reorganizing tomorrow's schedule...",
      reassignDone: "4 sessions reassigned to Andrea Kiss. Clients notified.",
      user4: "Thank you!",
      ai4: "You're welcome! The team is running efficiently. Let me know if you need anything! 🧘",
      inputPlaceholder: "Ask about your team...",
    },
  },
  pageUi: {
    breadcrumb: {
      home: "Home",
      forWhom: "Who is it for?",
    },
    hero: {
      badgePrefix: "Bespire",
      soloTag: "✓ For solo {prof}s",
      teamTag: "✓ For teams of {min}–{max}+",
      ctaPrimary: "Start now – free",
      ctaCallback: "Request a callback",
      noCard: "No credit card",
      trial: "3-day free trial · 30 gift AI credits",
    },
    valueProps: {
      badge: "Why Bespire?",
      title: "Why {profPlural} choose",
      titleHighlight: "Bespire",
    },
    scales: {
      badge: "One AI assistant, two sizes",
      title: "Scales with you — from one to {max}+ professionals",
      subtitle: "Whether you work solo or in a team — same account, same price. Unlimited team members, each with their own view, you with full visibility.",
      soloDayTitle: "A day in the life of a {prof}",
      teamDayTitle: "A day as the owner of a {teamLabel}",
      soloGains: "What you gain working solo",
      teamGains: "What you gain working as a team",
      noUpcharge: "You don't pay more for a team.",
      noUpchargeBody: "Whether 1 or {max}+ professionals — same 39 EUR / month. Unlimited team members, unlimited clients.",
    },
    howItWorks: {
      badge: "How it works",
      title: "A live {profGen} site in 4 steps",
      subtitle: "Launch via chat in 10 minutes — or we set it up for you in 30 minutes.",
    },
    monetization: {
      badge: "Revenue streams",
      title: "What can you sell as a {profGen}?",
      subtitle: "Examples calibrated to real market prices. One AI assistant runs it all — not separate tools.",
    },
    integrations: {
      worksWith: "Works with your existing",
      tools: "tools.",
    },
    objections: {
      badge: "Common concerns",
      title: "Let's address them",
    },
    pricing: {
      badge: "Simple pricing",
      price: "39 EUR",
      priceUnit: "/ month",
      description: "Full AI assistant: brand, website, blog/SEO, marketing, email, booking, CRM, team — in one place. 50 AI credits / month, unlimited team members, unlimited clients. 3-day free trial, 30 gift credits, no credit card.",
      bullet1: "1–{max}+ professionals in one account",
      bullet2: "Brand, website, blog, marketing, email, booking, CRM in one",
      bullet3: "Full EU language support, EU servers",
      detailedCta: "Detailed pricing",
    },
    setup: {
      badge: "Two paths to launch",
      title: "DIY — or we set it up for you",
      diyBadge: "10 minutes",
      diyTitle: "DIY — build it via chat",
      diyBody: "Chat with the AI assistant in your language — it does everything: brand, site, blog, marketing, booking, email, CRM. 3-day free trial, 30 gift credits, no credit card.",
      diyBullet1: "Live site in 10 minutes",
      diyBullet2: "No credit card to start",
      diyBullet3: "Always editable via chat",
      diyCta: "Start now",
      doneBadge: "30 minutes",
      doneTitle: "Setup — we do it",
      doneBody: "We import your existing clients, passes, and calendar. By the time you wake up tomorrow, everything is live.",
      formName: "Your name",
      formPhone: "Phone number",
      formCta: "Request a callback",
      formFootnote: "We'll call you back within 24 hours on business days.",
      thankYou: "Thank you! We'll call you on the number you provided within 24 hours.",
    },
    similar: {
      badge: "Similar professions",
      title: "These also benefit from Bespire",
    },
    aiScope: {
      badge: "One AI assistant — not just booking and website",
      title: "Everything you used 8–10 separate tools for",
      subtitle: "One chat, one account, one monthly price. The AI assistant runs your entire digital operation — not just booking and website.",
      overrideNote: "Examples for {profGen}",
      categories: [
        {
          key: "brand",
          icon: "Globe",
          label: "Brand & website",
          defaults: [
            "Logo, colors, custom domain in 10 minutes",
            "New service or price via chat, live instantly",
            "Before-after gallery and reviews on autopilot",
          ],
        },
        {
          key: "booking",
          icon: "Calendar",
          label: "Booking & passes",
          defaults: [
            "Online + in-person payment with auto invoicing",
            "Packages, passes, waitlist, no-show protection",
            "Google Calendar and Meet sync",
          ],
        },
        {
          key: "content",
          icon: "Sparkles",
          label: "AI content & social",
          defaults: [
            "Weekly Instagram, Facebook and TikTok post plan",
            "Hashtag, caption and image ideas for every post",
            "Storyboards for reels and short videos",
          ],
        },
        {
          key: "seo",
          icon: "TrendingUp",
          label: "SEO & Google Business",
          defaults: [
            "Blog and service pages targeting local keywords",
            "Auto Google Business posts and photos",
            "Meta titles, alt text, sitemap kept fresh",
          ],
        },
        {
          key: "email",
          icon: "Mail",
          label: "Email & WhatsApp",
          defaults: [
            "Confirmation and 24-hour reminder templates",
            "Follow-ups, feedback and review requests",
            "Win-back campaigns for inactive clients",
          ],
        },
        {
          key: "crm",
          icon: "Users",
          label: "Client CRM",
          defaults: [
            "Every client gets a profile, history, notes",
            "Source tracking: where they came from, what they spent",
            "Pass status and expiry alerts",
          ],
        },
        {
          key: "analytics",
          icon: "BarChart3",
          label: "Analytics & decisions",
          defaults: [
            "Weekly revenue, utilisation and source report",
            "Which service/channel drives bookings",
            "AI suggestions for pricing, slots and campaigns",
          ],
        },
        {
          key: "team",
          icon: "ShieldCheck",
          label: "Team & permissions",
          defaults: [
            "Unlimited team members, same monthly price",
            "Role-based access: owner, manager, staff",
            "Performance and revenue per team member",
          ],
        },
      ],
    },
    chatExampleAria: "5 stars",
    fiveStars: "5 stars",
  },
};
